// 📋 TEMPLATES ESPECÍFICOS POR TIPO DE EXAME
// Sistema: HEPATO TX RONDÔNIA
// Arquivo: src/modules/ia-analise/services/templatesExames.ts

import type { CenarioClinico } from "./enquadramentoClinico";

/**
 * Detecta a categoria do exame baseado no tipo
 */
// B385 (AI_156): `dica` = nome do arquivo. A biópsia de 01/11 estava cadastrada
// como "Outros" e caiu no template GENÉRICO — perdeu a seção de IHQ e a
// hierarquia da conclusão do patologista do template dedicado. O nome do PDF
// ("...BIOPSIA.pdf") dizia o que era. Só é consultado quando o TIPO não decide.
export function detectarCategoriaExame(tipo: string, dica = ""): string {
  const porTipo = detectarCategoriaPorTexto(tipo);
  if (porTipo !== "generico" && porTipo !== "imagem_hepatica") return porTipo;
  if (dica) {
    const porDica = detectarCategoriaPorTexto(dica);
    if (porDica === "biopsia") return porDica; // só o que o nome do arquivo decide com segurança
  }
  return porTipo;
}

function detectarCategoriaPorTexto(tipo: string): string {
  const tipoLower = (tipo || "").toLowerCase();

  // Biópsia / Anatomopatológico
  if (
    tipoLower.includes("biopsia") ||
    tipoLower.includes("biópsia") ||
    tipoLower.includes("anatomopatol") ||
    tipoLower.includes("histopatol") ||
    // B385: rótulos reais de PDF/tipo que são patologia e não casavam
    tipoLower.includes("patologia") ||
    tipoLower.includes("imuno-histoqu") ||
    tipoLower.includes("imunohistoqu") ||
    tipoLower.includes("ihq")
  ) {
    return "biopsia";
  }

  // Ecoendoscopia / USE — DEVE ser testada ANTES de "endoscopia": a string
  // "ecoendoscopia" contém a substring "endoscopia"; sem esta precedência ela
  // cairia no template de EDA (focado em varizes) em vez do template próprio de
  // EUS (pâncreas, via biliar distal, linfonodos, estadiamento). B305/AI_139.
  if (
    tipoLower.includes("ecoendoscopia") ||
    tipoLower.includes("eco endoscopia") ||
    tipoLower.includes("ultrassom endoscópico") ||
    tipoLower.includes("use ")
  ) {
    return "ecoendoscopia";
  }

  // Endoscopia Digestiva Alta (EDA). "colonoscopia" foi REMOVIDA daqui: tem
  // template próprio (getTemplateColonoscopia, abaixo) e era engolida por este
  // bloco (que a listava explicitamente), virando código morto. "colonoscopia"
  // não contém a substring "endoscopia", então basta não listá-la. B305/AI_139.
  if (tipoLower.includes("endoscopia") || tipoLower.includes("eda")) {
    return "endoscopia";
  }

  // Cardiológico
  if (
    tipoLower.includes("ecocardiograma") ||
    tipoLower.includes("eco ") ||
    tipoLower.includes("cateterismo")
  ) {
    return "cardiologico";
  }

  // Pulmonar
  if (
    tipoLower.includes("pulmonar") ||
    tipoLower.includes("espirometria") ||
    tipoLower.includes("prova de função") ||
    tipoLower.includes("prova função")
  ) {
    return "pulmonar";
  }

  // Elastografia
  if (tipoLower.includes("elastografia") || tipoLower.includes("fibroscan")) {
    return "elastografia";
  }

  // PET-CT / Cintilografia / Mapeamento Ósseo (medicina nuclear)
  // AI_138 (B301 cont.): "Mapeamento Ósseo" (cintilografia óssea) não continha
  // "cintilografia" e caía no template de fígado — agora entra no nuclear.
  if (
    tipoLower.includes("pet") ||
    tipoLower.includes("cintilografia") ||
    tipoLower.includes("cintilo") ||
    tipoLower.includes("mapeamento ósseo") ||
    tipoLower.includes("mapeamento osseo")
  ) {
    return "nuclear";
  }

  // Intervenção (TACE, Ablação)
  if (
    tipoLower.includes("quimioemboliza") ||
    tipoLower.includes("tace") ||
    tipoLower.includes("ablação") ||
    tipoLower.includes("ablacao")
  ) {
    return "intervencao";
  }

  // Tórax (TC, RM, Rx) — categoria neutra; cenário clínico decidido
  // pelo gerarPromptExameImagem via detectarCenarioClinico (AI_46)
  if (
    tipoLower.includes("tórax") ||
    tipoLower.includes("torax") ||
    tipoLower.includes("tc tórax") ||
    tipoLower.includes("tc torax") ||
    tipoLower.includes("tomografia de tórax") ||
    tipoLower.includes("tomografia torax")
  ) {
    return "torax";
  }

  // CPRE (Colangiopancreatografia Retrógrada Endoscópica)
  if (
    tipoLower.includes("cpre") ||
    tipoLower.includes("colangiopancreatografia") ||
    tipoLower.includes("ercp")
  ) {
    return "cpre";
  }

  // Colangiografia Transparieto-hepática (CTPH)
  if (
    tipoLower.includes("transparieto") ||
    tipoLower.includes("ctph") ||
    tipoLower.includes("colangiografia percutânea") ||
    tipoLower.includes("colangiografia percutanea") ||
    tipoLower.includes("drenagem biliar percutânea") ||
    tipoLower.includes("drenagem biliar percutanea")
  ) {
    return "ctph";
  }

  // Colonoscopia (separar de EDA)
  if (tipoLower.includes("colonoscopia")) {
    return "colonoscopia";
  }

  // Documentos e exames NÃO hepático-abdominais → template genérico adaptativo
  // (Fase D, AI_138). Cobre crânio, raio-X e o "Outros" do dropdown (ficha de
  // doador, resposta da central de transplantes, relatórios administrativos).
  // "TC Outros"/"RM Outros"/Ultrassom genérico permanecem no template de fígado
  // (podem ser abdominais) — por isso "outros" é casado por IGUALDADE EXATA,
  // não por substring. Colocado APÓS as categorias específicas (ex.: "tórax"
  // captura "radiografia de tórax" antes de chegar aqui).
  if (
    tipoLower.includes("crânio") ||
    tipoLower.includes("cranio") ||
    tipoLower.includes("raio-x") ||
    tipoLower.includes("raio x") ||
    tipoLower.includes("radiografia") ||
    tipoLower.trim() === "rx" ||
    tipoLower.trim() === "outros"
  ) {
    return "generico";
  }

  // Imagem hepática padrão (TC, RM, US, Doppler)
  return "imagem_hepatica";
}

export function getTemplateBiopsia(): string {
  return `
  ## ⚠️ REGRAS ESPECÍFICAS PARA BIÓPSIA/ANATOMOPATOLÓGICO:
  
  ### 🔬 FOCO EXCLUSIVO EM HISTOPATOLOGIA:
  - ✅ Grau histológico (Edmondson-Steiner para CHC)
  - ✅ Padrão arquitetural (trabecular, pseudoacinar, sólido, etc.)
  - ✅ Diferenciação celular (bem, moderadamente, pouco diferenciado)
  - ✅ Invasão vascular MICROSCÓPICA (se descrita)
  - ✅ Invasão perineural
  - ✅ Necrose tumoral
  - ✅ Margens (se ressecção)
  - ✅ Tecido hepático adjacente (fibrose, cirrose, inflamação)
  - ✅ Imuno-histoquímica (se realizada)
  - ✅ **Rejeição pós-transplante (se biópsia de enxerto)**
  
  ### 🚫 NÃO MENCIONAR EM BIÓPSIA:
  - ❌ Critérios de Milão (são radiológicos)
  - ❌ Classificação LI-RADS (são radiológicos)
  - ❌ Trombose de veia porta (avaliação por imagem)
  - ❌ Shunts porto-sistêmicos
  - ❌ Ascite
  - ❌ Esplenomegalia
  - ❌ Dimensões macroscópicas da lesão (usar dados da imagem)
  
  ### 🔴 BIÓPSIA DE ENXERTO HEPÁTICO - REJEIÇÃO:
  
  Se a biópsia for de **enxerto hepático pós-transplante**, avaliar:
  
  #### CLASSIFICAÇÃO DE BANFF (RAI - Rejection Activity Index):
  | Componente | Achados | Score |
  |------------|---------|-------|
  | Inflamação portal | Linfócitos em alguns espaços-porta | 1 |
  | | Linfócitos em maioria dos espaços-porta | 2 |
  | | Expansão marcada de todos os espaços-porta | 3 |
  | Lesão de ductos biliares | Minoria de ductos com alterações | 1 |
  | | Maioria de ductos com alterações | 2 |
  | | Maioria com degeneração/necrose | 3 |
  | Endotelite venosa | Subendotelial em algumas vênulas | 1 |
  | | Subendotelial na maioria das vênulas | 2 |
  | | Endotelite com perivenulite | 3 |
  
  **Interpretação do RAI:**
  - **3-4:** Rejeição LEVE (indeterminada/mínima)
  - **5-6:** Rejeição MODERADA
  - **7-9:** Rejeição GRAVE
  
  #### TIPOS DE REJEIÇÃO:
  
  **1. REJEIÇÃO CELULAR AGUDA (T-cell mediated):**
  - Inflamação portal mista
  - Lesão de ductos biliares
  - Endotelite venosa
  - C4d geralmente NEGATIVO
  
  **2. REJEIÇÃO MEDIADA POR ANTICORPOS (AMR):**
  - C4d POSITIVO (deposição no endotélio portal/sinusoidal)
  - DSA (anticorpos doador-específicos) circulantes
  - Disfunção do enxerto
  
  **3. REJEIÇÃO CRÔNICA (Ductopênica):**
  - Perda de ductos biliares > 50%
  - Arteriopatia obliterativa
  - Fibrose progressiva
  
  ### 💊 REJEIÇÃO — ESCOPO DESCRITIVO (este laudo NÃO prescreve tratamento):
  
  Quando a biópsia de enxerto demonstrar achados de rejeição, este laudo é DESCRITIVO-INTERPRETATIVO e deve se limitar a:
  
  - **Classificar** o tipo de rejeição identificada pelo patologista (celular aguda, mediada por anticorpos, crônica/ductopênica)
  - **Classificar** o grau quando aplicável (RAI score se fornecido pelo patologista)
  - **Registrar** achados associados relevantes (C4d, DSA quando disponíveis, percentual de ductos afetados, arteriopatia)
  - **Correlacionar** com o quadro clínico-laboratorial do paciente (enzimas hepáticas, nível sérico de imunossupressor em uso, tempo pós-transplante)
  
  **Escopo explicitamente EXCLUÍDO deste laudo:**
  - Protocolo terapêutico específico (droga, dose, via, duração, número de pulsos)
  - Nível sérico alvo de imunossupressor
  - Esquema de desmame de corticoide
  - Indicação de plasmaférese, IVIG, rituximabe, bortezomibe, eculizumabe, timoglobulina
  - Decisão de retransplante
  - Prazo específico de biópsia de controle
  
  **Motivo**: decisão terapêutica em rejeição de enxerto depende de fatores não contidos no laudo histopatológico — esquema de imunossupressão atual, aderência, interações medicamentosas, infecções concomitantes, status virológico, função renal, função do enxerto, comorbidades. Essa decisão é competência do **comitê multidisciplinar de transplante / hepatologista assistente**, não deste laudo de análise de exame.
  
  **Linguagem apropriada**: ao invés de prescrever conduta, registrar que "o achado de [tipo/grau de rejeição] sugere necessidade de reavaliação da imunossupressão pelo time assistente, conforme protocolo do centro."
  
  ### 📝 ESTRUTURA DA RESPOSTA PARA BIÓPSIA:
  
  #### 1. RESUMO DO EXAME
  - Tipo de material (biópsia por agulha, ressecção, explante)
  - Data
  - Método de coloração
  
  #### 2. DESCRIÇÃO MACROSCÓPICA
  - Número e dimensões dos fragmentos
  - Aspecto do material
  
  #### 3. ACHADOS HISTOPATOLÓGICOS ESTRUTURADOS - ⚠️ SEÇÃO CRÍTICA
  
  **PRINCÍPIO FUNDAMENTAL**: nesta seção você NÃO produz diagnóstico histopatológico autônomo. Você está lendo um laudo assinado por um patologista — sua função é **extrair, estruturar e organizar fielmente** o que o patologista concluiu, preservando a linguagem diagnóstica original. O diagnóstico principal, o grau, o padrão arquitetural, a atividade mitótica e as características celulares DEVEM ser reportados conforme o patologista descreveu, não conforme você inferiria a partir do material.
  
  **Extrair e estruturar do laudo do patologista:**
  - Diagnóstico principal (preservando qualificadores: "sugere", "consistente com", "compatível com", "suspeito", "menos provável", "diferencial inclui")
  - Grau de diferenciação (se o patologista declarou: bem / moderadamente / pouco diferenciado, ou Edmondson-Steiner 1-4 para CHC)
  - Padrão arquitetural (se descrito: trabecular, pseudoacinar, sólido, escirroso, etc.)
  - Características celulares relevantes (conforme descritas no laudo)
  - Atividade mitótica (se quantificada pelo patologista)
  - Invasão vascular microscópica, invasão perineural, necrose, margens — conforme declarado no laudo
  - Se enxerto: tipo de rejeição identificada e RAI score (se fornecido pelo patologista)
  
  **Regra de preservação literal**: se o patologista usou "SUGERE carcinoma hepatocelular", você reporta "SUGERE carcinoma hepatocelular" — não promova para "CHC confirmado". Se usou "diferencial inclui colangiocarcinoma como menos provável", preserve a hierarquia exata.
  
  **REGIME DE OBSERVAÇÃO CRÍTICA (apenas em análise exploratória de exame individual)**:
  
  Esta é uma análise de exame — instrumento de apoio ao raciocínio clínico do médico assistente, NÃO um laudo oficial. Nesse contexto específico, se ao integrar o laudo do patologista com o contexto clínico-radiológico-laboratorial do paciente você identificar razão técnica concreta para observação ou hipótese alternativa, você PODE registrar em seção separada chamada **"Observação do auditor independente"**, respeitando rigorosamente as seguintes regras:
  
  1. **A seção 3 (estruturação fiel) permanece intocada** — a conclusão do patologista é preservada literalmente no campo CONCLUSAO do bloco ACHADOS_EXAME e em toda estruturação de achados. A observação vai em seção PRÓPRIA, separada, após a estruturação.
  
  2. **Base técnica obrigatória**: a observação só é registrada se houver âncora concreta em pelo menos UM destes elementos:
     - Marcador IHQ específico cujo padrão sugere hierarquia diagnóstica alternativa (GP3/Glypican-3, GS/Glutamina Sintetase, CD34 capilarização sinusoidal, Hep Par-1, Arginase-1, CK7, CK19, CEA policlonal padrão, CAM5.2 — marcadores inespecíficos como Ki-67 ou p53 isolados NÃO servem como âncora)
     - Discordância clínico-radiológica-patológica objetiva (ex.: lesão com LR-5 completo, AFP significativamente elevada, threshold growth documentado contradizendo IHQ de baixa qualidade)
     - Adequação amostral questionável declarada pelo próprio patologista ou inferível por parâmetros objetivos (fragmento exíguo, número de espaços-porta insuficiente, material fragmentado)
     - Divergência entre biópsias seriadas do mesmo paciente não explicada por tratamento intercorrente
  
  3. **Linguagem literal obrigatória de incerteza**: use **"aventa-se"**, **"pode ser pertinente considerar"**, **"sugere-se reavaliação"**, **"recomenda-se discussão em reunião multidisciplinar"**, **"vale ponderar segunda opinião patológica"**. NUNCA use linguagem categórica ("é na verdade X", "o diagnóstico correto seria Y", "o patologista errou", "contradiz").
  
  4. **Terminar sempre com ação clínica concreta**: a observação deve terminar apontando conduta tangível — reconvocação do patologista, solicitação de IHQ complementar, revisão do bloco original, segunda opinião em centro de referência, discussão multidisciplinar. Observação sem conduta associada não deve ser registrada.
  
  5. **Escopo proibido**: esta seção NUNCA deve ser usada para substituir o diagnóstico do patologista em qualquer campo estruturado, para produzir diagnóstico alternativo categórico, ou para contaminar o campo CONCLUSAO do bloco ACHADOS_EXAME. A observação é complementar, descartável pelo médico assistente se não fizer sentido, e serve exclusivamente como aviso de auditoria para orientar eventual reconvocação patológica.
  
  6. **Quando NÃO registrar**: se não houver âncora técnica objetiva (marcador IHQ específico, discordância clínico-radiológica clara, adequação questionável, divergência inter-biópsias), o silêncio é a postura correta. Observação sem base é ruído clínico — omita a seção inteiramente nesse caso.
  
  **Este regime de observação existe porque**: análise de exame é instrumento de raciocínio clínico do médico assistente. Ele pode querer saber se há razão técnica para reconvocar o patologista — é dele a decisão de fazê-lo ou não. Em laudo formal (Comitê, INSS, Situação Especial), este regime NÃO se aplica: o pipeline de laudo preserva a conclusão do patologista sem acréscimos, porque o documento é formal e sai da assinatura do médico assistente.
  
  #### 4. TECIDO HEPÁTICO ADJACENTE
  - Fibrose (grau se disponível)
  - Inflamação
  - Esteatose
  - Sinais de cirrose
  
  #### 5. MARCADORES IMUNO-HISTOQUÍMICOS (se realizados)
  - Resultados e interpretação
  - **C4d para rejeição humoral**
  
  #### 6. ACHADOS CRÍTICOS
  - Diagnóstico definitivo
  - Grau prognóstico
  - Dados faltantes importantes
  
  #### 7. CORRELAÇÕES CLÍNICAS E SUGESTÕES DE AVALIAÇÃO
  
  **Se biópsia de tumor (ressecção, agulha ou explante):**
  - Correlacionar com achados de imagem já disponíveis (dimensões, multifocalidade, invasão vascular macroscópica)
  - Sinalizar se há discordância significativa entre histopatologia e imagem (ex.: explante com maior número de nódulos que o pré-Tx — relevante para prognóstico e vigilância)
  - Sugerir avaliação adicional se a histopatologia alterar o enquadramento (ex.: upgrade de diferenciação, presença de mVI antes não documentada, satelitose)
  
  **Se biópsia de enxerto com REJEIÇÃO:**
  - Registrar o tipo e grau de rejeição identificado pelo patologista (celular aguda, mediada por anticorpos, crônica; RAI se disponível; C4d status)
  - Correlacionar com o contexto clínico-laboratorial do paciente (tempo pós-Tx, enzimas hepáticas atuais, nível sérico de imunossupressor em uso, tendência recente)
  - Sinalizar fatores que o comitê deve avaliar ao definir conduta: potencial não-aderência (nível de imunossupressor baixo ou flutuante), interações medicamentosas recentes, infecção concomitante (especialmente CMV, EBV)
  - Sugerir que a definição do esquema terapêutico (pulsoterapia, ajuste de imunossupressor, plasmaférese, timoglobulina, avaliação de retransplante) seja feita pelo time assistente/comitê multidisciplinar, conforme protocolo do centro — **este laudo não prescreve conduta**
  
  **Se biópsia de fígado nativo em doença crônica (cirrose/hepatite/colestase):**
  - Correlacionar estadiamento (METAVIR/Ishak/Laennec) com elastografia e imagem disponíveis
  - Sinalizar discordâncias relevantes (ex.: biópsia com fibrose menor que imagem sugere — possível erro amostral)
  - Sugerir contextualização em discussão clínica quando o achado impacta decisão de listagem ou priorização
  
  #### 8. RESUMO ESTRUTURADO
  
  **Selecionar a estrutura conforme o material analisado. A escolha é mutuamente exclusiva — emitir apenas UMA das estruturas abaixo.**
  
  **Estrutura A — biópsia por agulha, biópsia de enxerto hepático, biópsia excisional, peça cirúrgica não-explante:**
  
  \`\`\`ACHADOS_EXAME
  TIPO: Anatomopatológico - [especificar]
  DATA: [data]
  DIAGNOSTICO: [diagnóstico histopatológico]
  GRAU_HISTOLOGICO: [Grau 1-4 ou bem/moderadamente/pouco diferenciado]
  PADRAO_ARQUITETURAL: [trabecular, pseudoacinar, sólido, etc.]
  INVASAO_VASCULAR_MICRO: [SIM / NÃO / NÃO AVALIADA]
  INVASAO_PERINEURAL: [SIM / NÃO / NÃO AVALIADA]
  NECROSE: [percentual se descrito / NÃO]
  MARGENS: [livres / comprometidas / NÃO APLICÁVEL]
  FIGADO_ADJACENTE: [descrição: fibrose, cirrose, inflamação]
  IMUNOHISTOQUIMICA: [resultados ou NÃO REALIZADA]
  REJEICAO_TIPO: [CELULAR AGUDA / HUMORAL / CRÔNICA / NÃO APLICÁVEL]
  REJEICAO_GRAU: [LEVE / MODERADA / GRAVE / NÃO APLICÁVEL]
  RAI_SCORE: [3-9 / NÃO APLICÁVEL]
  C4D: [POSITIVO / NEGATIVO / NÃO REALIZADO]
  TRATAMENTO_INDICADO: [protocolo específico]
  CONCLUSAO: [resumo diagnóstico em 1-2 frases]
  \`\`\`
  
  **Estrutura B — explante hepático (produto de hepatectomia total do receptor no contexto de transplante). Pistas textuais de ativação: "produto de explante hepático", "fígado, explante", "hepatectomia total", peso do fígado inteiro com dimensões globais (tipicamente > 800 g), amostragem em bloco de lobo direito + lobo esquerdo + hilo hepático. NÃO usar Estrutura B para biópsia de enxerto.**
  
  \`\`\`ACHADOS_EXAME
  TIPO: Anatomopatológico - Explante hepático
  DATA: [data]
  DIAGNOSTICO: [diagnóstico histopatológico literal; preservar bifurcação quando houver, conforme regra de preservação literal em diagnóstico bifurcado]
  GRAU_HISTOLOGICO: [Grau 1-4 ou bem/moderadamente/pouco diferenciado]
  PADRAO_ARQUITETURAL: [trabecular, pseudoacinar, sólido, escirroso, etc.]
  INVASAO_VASCULAR_MICRO: [SIM / NÃO / NÃO AVALIADA]
  INVASAO_PERINEURAL: [SIM / NÃO / NÃO AVALIADA]
  NECROSE: [percentual se descrito / NÃO]
  MARGENS: [em explante tipicamente NÃO APLICÁVEL; se o patologista descrever margens de parênquima, registrar literalmente]
  FIGADO_ADJACENTE: [descrição: fibrose, cirrose, inflamação — complementar com HEPATOPATIA_SUBJACENTE abaixo]
  IMUNOHISTOQUIMICA: [resultados ou NÃO REALIZADA; registrar IHQ pendente quando solicitada explicitamente pelo patologista]
  REJEICAO_TIPO: NÃO APLICÁVEL
  REJEICAO_GRAU: NÃO APLICÁVEL
  RAI_SCORE: NÃO APLICÁVEL
  C4D: NÃO REALIZADO
  TRATAMENTO_INDICADO: NÃO APLICÁVEL
  CONCLUSAO: [preservar literalmente a conclusão do patologista; em diagnóstico bifurcado, preservar bifurcação com recomendação de IHQ quando solicitada]
  FOCALIDADE_TUMORAL: [UNIFOCAL / MULTIFOCAL / NÃO APLICÁVEL]
  TAMANHO_MACRO_CM: [tamanho macroscópico em cm; multi-valor se multifocal, ex.: "3,5 / 1,8 / 1,7"]
  TAMANHO_VIAVEL_CM: [tamanho de neoplasia viável residual em cm; pode ser "0,2" ou "não avaliável - totalmente necrótico"]
  EFEITO_TERAPEUTICO_PCT_NECROSE: [% de necrose por nódulo; multi-valor se multifocal, ex.: "95% / 100% / 100%"; qualitativo se não quantificado]
  SATELITOSE: [PRESENTE / AUSENTE / NÃO APLICÁVEL]
  INVASAO_VASCULAR_MACRO: [PRESENTE / AUSENTE / PRÉVIA-TRATADA / NÃO APLICÁVEL]
  EXTENSAO_ESTRUTURAS_ADJACENTES: [descritivo: diafragma, suprarrenal, parede, muscular esquelético aderido; "ausente" se limitado ao fígado]
  LINFONODOS_HILARES: [formato "N0 (0/2)", "N1 (1/3)" ou "não avaliados"]
  PTNM_COMPLETO: [pTNM literal com prefixo "y" quando aplicável, ex.: "ypT4 pN0", "pT2 pN0"]
  TUMOR_SINCRONICO: [descritivo se presente, ex.: "neoplasia em seg VII 1,7 cm, aspecto sugestivo de iCCA OU cHCC-CCA combinado, IHQ pendente"; "AUSENTE" se único tipo histológico]
  MILAO_EXPLANTE_VIAVEL: [ATENDE / NÃO ATENDE / NÃO APLICÁVEL - aferido exclusivamente sobre tumor viável residual, conforme regra de Milão retrospectivo]
  HEPATOPATIA_SUBJACENTE: [etiologia + padrão histológico sobreposto quando descrito, ex.: "VHC com componente leve de esteato-hepatite", "cirrose esteato-hepatítica (MASH/ASH)"]
  \`\`\`
  
  ---
  
  ### 🏥 EXPLANTE HEPÁTICO — BLOCO ESPECÍFICO
  
  **ATIVAÇÃO**: aplica-se quando o material é produto de explante hepático (hepatectomia total no contexto de transplante — fígado do receptor). Pistas textuais: "produto de explante hepático", "fígado, explante", "hepatectomia total", peso do fígado inteiro com dimensões globais (tipicamente >800 g), amostragem em bloco de lobo direito + lobo esquerdo + hilo hepático. Este bloco NÃO se aplica a biópsia de enxerto (fígado do doador pós-Tx) — para isso, usar o bloco REJEIÇÃO acima.
  
  #### EIXOS OBRIGATÓRIOS DE ESTRUTURAÇÃO
  
  A conclusão do patologista permanece soberana (regra geral aplica). A análise em explante estrutura adicionalmente os seguintes eixos quando presentes no laudo:
  
  1. **Focalidade** (unifocal vs multifocal) e número de nódulos, com localização por segmento.
  2. **Tamanho macroscópico vs tamanho de neoplasia viável residual**: após tratamento locorregional (TACE, ablação, radioembolização) ou sistêmico (sorafenib, lenvatinib, atezolizumabe-bevacizumabe), o nódulo macroscópico pode ter dimensão muito maior que o foco viável. Reportar **ambos** quando o patologista os separa; nunca substituir um pelo outro.
  3. **Efeito terapêutico em % de necrose por nódulo** (ex.: "95% necrose no maior, 100% nos demais"). Preservar a quantificação literal. Se apenas qualitativa ("extensa necrose coagulativa"), reportar o descritor original.
  4. **Satelitose** (presença/ausência) — achado adverso distinto de multifocalidade.
  5. **Invasão vascular MACROSCÓPICA** — campo distinto de mVI: trombose tumoral portal ou em veias hepáticas (viável ou prévia-tratada/em recanalização), material necrótico tumoral intravascular em vasos hilares. Registrar mesmo quando totalmente necrótica (evidencia invasão vascular macroscópica prévia ao tratamento).
  6. **Extensão para estruturas adjacentes**: diafragma, suprarrenal, parede, tecidos conjuntivo-adiposo ou muscular esquelético aderidos — determinante para pT4.
  7. **Linfonodos hilares**: reportar com fração amostrada (ex.: "00/02" = pN0 de 2 linfonodos) ou "não avaliados" se ausente do laudo.
  8. **Prefixo "y" no pTNM** (ex.: ypT2 pN0, ypT4 pN0) — marcador histológico de neoadjuvância/terapia pré-Tx. Preservar literalmente.
  9. **Hepatopatia subjacente confirmada histologicamente**: etiologia (VHC, VHB, MASLD, álcool, autoimune, colestática) + padrão sobreposto quando descrito ("padrão esteato-hepatítico", "corpúsculos de Mallory-Denk", "colangiopatia"). Campo distinto do diagnóstico tumoral.
  
  #### MILÃO RETROSPECTIVO EM EXPLANTE
  
  Aferição exclusivamente sobre **tumor viável residual** (não sobre dimensão macroscópica). Justificativa técnica: após tratamento eficaz, o volume macroscópico agrega necrose coagulativa, fibrose e calcificações que não representam doença ativa; a carga tumoral prognosticamente relevante é a viável.
  
  Formato: "Em aferição sobre tumor viável residual, a peça [atende / não atende] aos critérios de Milão (≤ 3 nódulos; nódulo maior ≤ 3 cm; sem invasão vascular macroscópica; sem extensão extra-hepática)." Quando algum critério falha, explicitar qual.
  
  **Natureza retrospectiva descritiva**: paciente já transplantado. NÃO reinterpretar candidatura ao Tx à luz do achado patológico, NÃO qualificar a indicação como correta ou incorreta. O registro orienta apenas vigilância oncológica pós-Tx e estratificação prognóstica, conforme competência do comitê multidisciplinar.
  
  #### PRESERVAÇÃO LITERAL EM DIAGNÓSTICO BIFURCADO
  
  Quando o patologista expressa um diagnóstico entre duas ou mais hipóteses e solicita IHQ complementar (ex.: "aspecto sugestivo de colangiocarcinoma intra-hepático ou carcinoma hepatocelular-colangiocarcinoma combinado — recomenda-se imuno-histoquímica"), preservar a bifurcação **literalmente** no campo CONCLUSAO. Não escolher uma das hipóteses, não suprimir a outra, não resolver a incerteza do patologista por raciocínio próprio. Registrar a recomendação de IHQ como pendência explícita.
  
  #### GATILHOS AUTOMÁTICOS DE OBSERVAÇÃO DO AUDITOR INDEPENDENTE
  
  Em análise exploratória de explante (não em laudo formal), **invocar automaticamente** o regime de "Observação do auditor independente" (definido na Seção 3 da ESTRUTURA DA RESPOSTA acima) quando o laudo revelar **qualquer** das situações abaixo:
  
  - **Tumor sincrônico com histologia distinta** (ex.: CHC + iCCA, CHC + cHCC-CCA combinado, CHC + metástase) — implicação prognóstica e de vigilância pós-Tx difere de CHC multifocal puro.
  - **Upgrade patológico verdadeiro (achado histológico novo vs documentação pré-Tx)**: achado que o pré-Tx não sabia. Exemplos: maior número de nódulos viáveis, mVI antes não identificada, satelitose antes não descrita, upgrade de diferenciação, tumor sincrônico de histologia distinta descoberto apenas no explante. Esta categoria pressupõe que o histórico de imagens/biópsias pré-Tx foi cruzado e o achado é genuinamente novo.
  - **Persistência histológica de doença adversa previamente tratada**: achado que **confirma retrospectivamente** doença adversa já conhecida e tratada antes do Tx, sem desaparecimento real do substrato. Exemplos: material necrótico tumoral intravascular ou trombose em recanalização em paciente com trombose tumoral prévia tratada (mesmo que a última imagem pré-Tx não mostrasse trombose residual — a resposta imagenológica não equivale a ausência histológica do substrato); nódulo majoritariamente necrótico com foco viável residual em paciente com tratamento locorregional prévio documentado; calcificações distróficas e reação xantogranulomatosa compatíveis com efeito terapêutico. Esta categoria NÃO é upgrade — é persistência histológica de achado já conhecido. Merece observação porque afeta vigilância pós-Tx (risco de recorrência aumentado mesmo após downstaging eficaz), mas com linguagem distinta do upgrade verdadeiro.
  - **Diagnóstico bifurcado com IHQ complementar pendente** explicitamente solicitada pelo patologista.
  
  #### PRINCÍPIO DE ANCORAGEM FACTUAL COMPARATIVA
  
  Afirmações comparativas sobre o pré-Tx — "não documentado pré-Tx", "antes não identificado", "achado novo no explante", "sem correspondência em imagens prévias", "não descrito previamente" — **só podem ser feitas se ancoradas em elemento concreto do contexto do paciente disponível à análise** (exames prévios listados, contexto MECR-RAG, dados de consultas anteriores). Na ausência de ancoragem concreta, usar linguagem neutra de correlação, não de afirmação factual:
  
  - ✅ **Permitido com ancoragem**: "na última TC pré-Tx de [data citada] não se descrevia trombose tumoral residual, achado agora presente histologicamente sob forma de material necrótico intravascular — aventa-se persistência histológica de doença previamente tratada".
  - ✅ **Permitido sem ancoragem (linguagem neutra)**: "presença de material necrótico tumoral intravascular hilar compatível com efeito terapêutico sobre invasão vascular macroscópica — achado cuja correlação com documentação pré-Tx deve ser revisada pelo comitê para estratificação de vigilância pós-Tx".
  - ❌ **Proibido sem ancoragem**: "invasão vascular macroscópica não documentada no pré-Tx" (afirmação factual comparativa sem base verificável no contexto disponível).
  
  **Motivo**: modelos de linguagem podem gerar afirmações comparativas por pattern-matching ("mVI macro em explante" → inferência automática "não estava no pré-Tx") em vez de por verificação contra o contexto. Em análise de explante, a distinção entre upgrade verdadeiro e persistência de doença tratada depende crítica e exatamente dessa ancoragem — e errar essa distinção reintroduz, sob nova forma, a falha de "inferência retrospectiva por desfecho" já catalogada como categoria 4 das seis falhas do auditor independente (AI_35).
  
  Em cada caso, a observação deve: (1) preservar integralmente a conclusão literal do patologista no campo CONCLUSAO; (2) usar linguagem de incerteza ("aventa-se", "pode ser pertinente considerar", "sugere-se revisão"); (3) terminar com ação clínica concreta — discussão em reunião multidisciplinar hepatobiliopancreática/transplante, IHQ complementar, estratificação de vigilância pós-Tx. NUNCA qualificar a indicação pretérita do transplante.
  
  #### ESCOPO EXPLICITAMENTE EXCLUÍDO EM LAUDO DE EXPLANTE
  
  - Prescrição de intervalos específicos de vigilância por imagem pós-Tx (TC com intervalo X, RM a cada Y meses).
  - Indicação de tratamento adjuvante sistêmico específico (droga, dose, linha).
  - Reinterpretação retrospectiva da candidatura ao Tx ("deveria ter sido listado / não deveria").
  - Qualquer recomendação de imunossupressão (ajuste, nível-alvo, troca de agente).
  - Decisão sobre retransplante em caso de recorrência futura.
  
  Decisões de vigilância, adjuvância e manejo pós-Tx são competência do comitê multidisciplinar de transplante e do hepatologista assistente, conforme protocolo do centro e guidelines vigentes.
  
  ---
  
  ### 🔬 HIERARQUIA DIAGNÓSTICA DO PATOLOGISTA — REGRA SOBERANA COM CLÁUSULA DE EXCEÇÃO
  
  Quando o exame analisado for laudo anatomopatológico, biópsia ou imuno-histoquímica (IHQ) com conclusão diagnóstica assinada por patologista, aplicar hierarquia abaixo:
  
  **PRINCÍPIO PADRÃO** (vale na esmagadora maioria dos casos): a conclusão do patologista é SOBERANA e DEFINITIVA. Os achados estruturados devem PRESERVAR EXATAMENTE a hierarquia diagnóstica e os qualificadores do patologista ("menos provável", "diferencial", "suspeito", "improvável", "confirmado"). NUNCA reinterpretar marcadores IHQ individuais (CK7, CK19, Hep Par-1, Arginase-1, CEA, GP3/Glypican-3, GS/Glutamina Sintetase, CD34, CAM5.2) para inverter a hierarquia listada pelo patologista. O campo CONCLUSAO do bloco ACHADOS_EXAME deve refletir literalmente o diagnóstico do patologista, sem suavização editorial e sem omissão de qualificadores.
  
  **CLÁUSULA DE EXCEÇÃO** — divergência fundamentada permitida APENAS quando AS TRÊS condições abaixo estiverem SIMULTANEAMENTE satisfeitas (lógica E, não OU):
  
  (a) HESITAÇÃO DECLARADA DO PATOLOGISTA: o próprio laudo declara amostragem questionável, fragmento exíguo, representatividade limitada, material insuficiente para conclusão definitiva, OU apresenta diagnóstico em formato de diferencial aberto sem hierarquia firme.
  
  (b) PAINEL IHQ ESPECÍFICO contradiz a hierarquia listada, com marcadores específicos válidos: GP3/Glypican-3, GS/Glutamina Sintetase, CD34 (capilarização sinusoidal), Hep Par-1, Arginase-1, CK7, CK19, CEA policlonal padrão membranoso/luminal, CAM5.2. Marcadores inespecíficos NÃO satisfazem esta condição: Ki-67, p53, vimentina isolada.
  
  (c) PRÉ-TESTE CLÍNICO-RADIOLÓGICO ALTO documentado em evidências INDEPENDENTES da conduta subsequente: LR-5 prévio à biópsia, threshold growth em imagens seriadas, cirrose com fator de risco (HBV, HCV, álcool, MASLD), AFP elevada, PIVKA-II elevada. NÃO incluir como evidência: tratamento locorregional (TACE, ablação, radioembolização), listagem em fila de transplante, decisão de comitê. Condutas posteriores são potencialmente consequência de um diagnóstico radiológico que pode estar errado — usá-las como validação constitui raciocínio circular.
  
  **COMPORTAMENTO QUANDO AS 3 CONDIÇÕES ESTÃO SIMULTANEAMENTE SATISFEITAS**: (1) o campo CONCLUSAO do bloco ACHADOS_EXAME DEVE conter a citação literal da conclusão do patologista, sem suavização; (2) adicionar nota técnica de divergência fundamentada na seção de análise (não no campo CONCLUSAO), listando os marcadores IHQ específicos e o contexto clínico-radiológico que motivam a discordância; (3) recomendar discussão multidisciplinar (MDT hepatobiliopancreática), segunda opinião patológica em centro de referência, IHQ adicional ou reavaliação do bloco. NUNCA inverter o diagnóstico do patologista no campo CONCLUSAO ou em qualquer campo estruturado do bloco ACHADOS_EXAME — a divergência é OBSERVAÇÃO COMPLEMENTAR, não SUBSTITUIÇÃO.
  
  **COMPORTAMENTO QUANDO QUALQUER UMA DAS 3 CONDIÇÕES NÃO ESTÁ SATISFEITA**: aplicar o PRINCÍPIO PADRÃO. Patologista é soberano. Nenhuma reinterpretação, nenhuma divergência, nenhuma observação contrária. A ausência de qualquer uma das 3 condições basta para impedir a invocação da cláusula de exceção.
  
  ---
  
  **⚠️ OBRIGATÓRIO — LABEL LITERAL:** A linha de abertura do bloco acima DEVE conter o label literal \`ACHADOS_EXAME\` após as três crases, exatamente como no exemplo. Não use apenas \`\`\` sozinho — o parser depende do label literal para recuperar a estrutura.
  `;
}

/**
 * Template para ENDOSCOPIA DIGESTIVA (EDA/Colonoscopia)
 */
export function getTemplateEndoscopia(): string {
  return `
  ## ⚠️ REGRAS ESPECÍFICAS PARA ENDOSCOPIA DIGESTIVA:
  
  ### 🔍 FOCO DA ANÁLISE ENDOSCÓPICA:
  - ✅ Varizes esofágicas (calibre, localização, sinais de risco)
  - ✅ Varizes gástricas (classificação de Sarin)
  - ✅ Gastropatia hipertensiva portal (leve, moderada, grave)
  - ✅ Úlceras / erosões
  - ✅ Esofagite
  - ✅ Hérnia hiatal
  - ✅ Lesões suspeitas / pólipos
  - ✅ Sinais de sangramento recente
  - ✅ Ectasia vascular antral (GAVE)
  
  ### 🩸 CLASSIFICAÇÃO DE VARIZES ESOFÁGICAS:
  | Grau | Descrição | Risco |
  |------|-----------|-------|
  | Pequeno porte (F1) | < 5mm, retificam à insuflação | Baixo |
  | Médio porte (F2) | 5-10mm, não retificam, < 1/3 do lúmen | Moderado |
  | Grosso calibre (F3) | > 10mm, > 1/3 do lúmen | Alto |
  
  ### 🫃 CLASSIFICAÇÃO DE VARIZES GÁSTRICAS (SARIN):
  | Tipo | Descrição | Tratamento Preferencial |
  |------|-----------|------------------------|
  | GOV1 | Continuação de varizes esofágicas pela pequena curvatura | Ligadura elástica |
  | GOV2 | Extensão para fundo gástrico | Cianoacrilato ou TIPS |
  | IGV1 | Varizes isoladas de fundo gástrico | **Cianoacrilato ou TIPS** |
  | IGV2 | Varizes isoladas em outros locais | Cianoacrilato |
  
  ### ⚠️ SINAIS DE RISCO DE SANGRAMENTO:
  - Red spots (manchas vermelho-cereja)
  - Red wale marks (vergões vermelhos)
  - Hematocisto
  - Varizes sobre varizes
  - **Ponto de fibrina / coágulo aderido** (sangramento recente)
  - **Histórico de balão de Sengstaken-Blakemore** (indica sangramento grave prévio)
  
  ### 🚨 INDICADORES DE GRAVIDADE EXTREMA:
  Se houver menção a:
  - Balão de Sengstaken-Blakemore → **Sangramento maciço prévio, alto risco de re-sangramento**
  - Paciente em IOT/UTI → **Instabilidade hemodinâmica prévia**
  - Múltiplas sessões de ligadura → **Doença refratária**
  
  ### 📝 ESTRUTURA DA RESPOSTA PARA ENDOSCOPIA:
  
  #### 1. RESUMO DO EXAME
  - Tipo (EDA, Colonoscopia)
  - Data
  - Sedação utilizada
  - **Contexto clínico** (UTI? Sangramento ativo?)
  
  #### 2. ESÔFAGO
  - Varizes (presença, grau, sinais de risco)
  - Mucosa esofágica
  - Esofagite
  
  #### 3. ESTÔMAGO
  - Gastropatia hipertensiva
  - Varizes gástricas (classificação de Sarin)
  - Lesões / erosões
  - GAVE
  
  #### 4. DUODENO
  - Mucosa
  - Lesões
  
  #### 5. ⚠️ CLASSIFICAÇÃO DE RISCO (OBRIGATÓRIO)
  
  **SEMPRE incluir uma das classificações abaixo:**
  
  🔴 **ALTO RISCO DE SANGRAMENTO:**
  - F3 (varizes grosso calibre)
  - F2 com sinais de risco (red spots, hematocisto)
  - IGV1/IGV2 de grande calibre
  - Ponto de fibrina / sangramento recente
  - Histórico de Sengstaken-Blakemore
  - Child-Pugh C
  → **Conduta: Intervenção URGENTE**
  
  🟡 **MODERADO RISCO:**
  - F2 sem sinais de risco
  - GOV1/GOV2 pequeno-médio calibre
  - Child-Pugh B
  → **Conduta: Profilaxia secundária + seguimento próximo**
  
  🟢 **BAIXO RISCO:**
  - F1 sem sinais de risco
  - Child-Pugh A
  → **Conduta: Profilaxia primária + seguimento regular**
  
  #### 6. INDICAÇÕES TERAPÊUTICAS (SEM PRESCRIÇÃO)
  
  ⚠️ **ESCOPO DESTA SEÇÃO:** Descrever **indicações e classes terapêuticas** associadas aos achados endoscópicos. **NÃO prescrever doses, vias, horários, metas hemodinâmicas nem prazos de TRATAMENTO.** A conduta prescritiva cabe ao médico assistente com base no contexto clínico completo (função renal, hemodinâmica atual, interações, protocolo institucional). Esta regra se aplica às classes terapêuticas — NSBB, IBP, antibióticos, suporte marcial, imunossupressores. **ATENÇÃO (AI_118):** a PERIODICIDADE DE REPETIÇÃO DO PRÓPRIO EXAME (quando repetir a EDA) NÃO é prescrição terapêutica — é OBRIGATÓRIA e deve ser informada com o prazo concreto do protocolo (ver 6.5).
  
  **6.1 Hipertensão portal — bloqueio neuro-humoral:**
  - **Indicação:** CSPH confirmada (HVPG ≥10 mmHg, elastografia hepática ≥25 kPa, presença de varizes ou descompensação prévia)
  - **Classe preferencial:** carvedilol (Baveno VII — superior ao propranolol em reduzir HVPG e prevenir descompensação)
  - **Alternativa:** propranolol (intolerância ou contraindicação ao carvedilol)
  - Iniciar APENAS após controle do sangramento agudo, quando aplicável
  - **Contraindicação relativa:** ascite refratária com hipotensão sintomática ("janela terapêutica de Krag") — reavaliar classe ou suspender
  - **Contraindicações absolutas:** sangramento ativo, bradicardia significativa, broncoespasmo grave
  - **Observação (Baveno VII):** em CSPH SEM varizes, NSBB previne o aparecimento de varizes e descompensação — considerar precocemente
  
  **6.2 Tratamento endoscópico:**
  - Varizes esofágicas / GOV1: **ligadura elástica**
  - GOV2 / IGV1 / IGV2: **cianoacrilato**
  
  **6.3 TIPS (shunt portossistêmico intra-hepático) — indicações:**
  - IGV1 / IGV2 de grande calibre
  - Sangramento refratário a tratamento endoscópico
  - Re-sangramento apesar de profilaxia otimizada
  - **Considerar fortemente se histórico de Sengstaken-Blakemore** (sangramento maciço prévio)
  
  **6.4 GAVE (se presente):**
  - **Argônio plasma coagulation (APC)** como modalidade endoscópica de escolha
  - Avaliar suporte marcial e necessidade transfusional conforme grau de anemia
  
  **6.5 PERIODICIDADE / QUANDO REPETIR A EDA (OBRIGATÓRIO — sempre estipular o prazo):**
  Selecione o cenário que corresponde a ESTE exame e ESCREVA o intervalo do PROTOCOLO abaixo (protocolo do serviço, base Baveno VII / AASLD). NUNCA escreva "conforme protocolo" sem o número, NÃO devolva esta definição a outro nível de análise e NÃO faça sugestão personalizada — apenas informe o prazo do protocolo para o cenário aplicável.

  | Cenário (pelos achados desta EDA) | Repetir a EDA em |
  |---|---|
  | Pós-ligadura, em programa de erradicação | a cada 2–4 semanas até erradicação das varizes |
  | Após erradicação das varizes | 1ª EDA de controle em 3–6 meses; depois a cada 6–12 meses |
  | Cirrótico SEM varizes (rastreio), compensado | a cada 2 anos (3 anos se etiologia controlada) |
  | Varizes de fino calibre (F1) SEM tratamento | a cada 1 ano (se em NSBB de profilaxia, vigilância endoscópica dispensável) |
  | Cirrose descompensada | anual |
  | Em lista de transplante | a cada 6–12 meses |

  Se mais de um cenário se aplicar (ex.: pós-ligadura E em lista), priorize a periodicidade do programa de erradicação. Cite o prazo explicitamente nesta seção e reflita-o na conclusão.
  
  **6.6 Achados não relacionados à hipertensão portal (gastrite, esofagite, úlceras, erosões, H. pylori, pólipos):**
  - Descrever o achado, sua relevância clínica e eventual correlação com o contexto pré/pós-transplante
  - **Não prescrever IBP, antibióticos, esquemas de erradicação, doses ou duração de terapia**
  - Limitar-se a indicar a **classe ou conduta genérica** quando apropriado (ex.: "indicada supressão ácida", "indicada investigação/erradicação se H. pylori positivo", "indicada pesquisa de fatores lesivos — AINE, corticoide, álcool") e a necessidade de correlação clínica
  
  #### 7. RESUMO ESTRUTURADO
  
  \`\`\`ACHADOS_EXAME
  TIPO: Endoscopia Digestiva Alta / Colonoscopia
  DATA: [data]
  VARIZES_ESOFAGICAS: [NÃO / F1 / F2 / F3 - localização]
  SINAIS_RISCO: [red spots, red wale, hematocisto, fibrina / AUSENTES]
  VARIZES_GASTRICAS: [NÃO / GOV1 / GOV2 / IGV1 / IGV2 - tamanho]
  GASTROPATIA_HIPERTENSIVA: [NÃO / LEVE / MODERADA / GRAVE]
  ESOFAGITE: [NÃO / Los Angeles A/B/C/D]
  ULCERAS: [NÃO / localização e características]
  POLIPOS: [NÃO / número, localização, tamanho]
  GAVE: [SIM / NÃO]
  HISTORICO_SANGRAMENTO_GRAVE: [SIM - Sengstaken / NÃO]
  CLASSIFICACAO_RISCO: [🔴 ALTO / 🟡 MODERADO / 🟢 BAIXO]
  INDICACAO_TIPS: [SIM - motivo / NÃO]
  CONCLUSAO: [resumo em 1-2 frases com risco e conduta principal]
  \`\`\`
  
  ---
  
  **⚠️ OBRIGATÓRIO — LABEL LITERAL:** A linha de abertura do bloco acima DEVE conter o label literal \`ACHADOS_EXAME\` após as três crases, exatamente como no exemplo. Não use apenas \`\`\` sozinho — o parser depende do label literal para recuperar a estrutura.
  `;
}

/**
 * Template para ECOCARDIOGRAMA / CATETERISMO
 */
export function getTemplateCardiologico(): string {
  return `
  ## ⚠️ REGRAS ESPECÍFICAS PARA AVALIAÇÃO CARDIOLÓGICA:
  
  ### ❤️ FOCO DA ANÁLISE - PRÉ-TRANSPLANTE HEPÁTICO:
  - ✅ Fração de ejeção (FE) - mínimo 40-50% para Tx
  - ✅ Hipertensão arterial pulmonar (HAP)
  - ✅ Síndrome hepatopulmonar
  - ✅ Cardiomiopatia (diferenciar tipo!)
  - ✅ Disfunção diastólica
  - ✅ Valvopatias
  - ✅ Derrame pericárdico
  - ✅ Pressão sistólica da artéria pulmonar (PSAP)
  - ✅ **Massas, trombos ou cistos intracardíacos**
  
  ### 🫀 CRITÉRIOS PARA TRANSPLANTE:
  | Parâmetro | Aceitável | Contraindicação Relativa | Contraindicação Absoluta |
  |-----------|-----------|--------------------------|--------------------------|
  | FE | > 50% | 40-50% | < 40% |
  | PSAP | < 45 mmHg | 45-60 mmHg | > 60 mmHg (HAP grave) |
  | HAP | Ausente | Leve-moderada | Grave (não responsiva) |
  
  ### 🔴 DIFERENCIAÇÃO DE CARDIOMIOPATIAS (OBRIGATÓRIO):
  
  **CARDIOMIOPATIA CIRRÓTICA (típica de hepatopatas):**
  - FE geralmente **preservada ou levemente reduzida** (> 40-50%)
  - **Disfunção diastólica predominante** (grau I-III)
  - Resposta inotrópica reduzida ao estresse
  - QT prolongado no ECG
  - Reversível após transplante hepático
  
  **CARDIOMIOPATIA DILATADA (doença cardíaca primária):**
  - FE **gravemente reduzida** (< 35-40%)
  - Dilatação de câmaras (VE, AE)
  - Hipocinesia difusa ou segmentar
  - **NÃO relacionada à cirrose** - doença cardíaca independente
  - **Contraindicação ao transplante hepático**
  
  **CARDIOMIOPATIA ISQUÊMICA:**
  - FE reduzida com **alterações segmentares**
  - Hipocinesia/acinesia em territórios coronarianos
  - História de DAC, IAM
  - Requer avaliação coronariana antes do Tx
  
  ⚠️ **IMPORTANTE:** Se FE < 40% com hipocinesia difusa e dilatação de câmaras, 
  classificar como **cardiomiopatia dilatada** (não cirrótica), pois este padrão 
  indica doença cardíaca primária que contraindica transplante hepático.
  
  ### 🚨 MASSAS E ESTRUTURAS INTRACARDÍACAS (ATENÇÃO ESPECIAL):
  
  Se identificar massa, trombo ou cisto intracardíaco:
  
  1. **TROMBO INTRACARDÍACO:**
     - Localização (VE, AE, VD, AD)
     - Tamanho e mobilidade
     - Risco de embolização
     - **Anticoagulação obrigatória antes de qualquer procedimento**
     - **Contraindicação relativa/absoluta ao Tx até resolução**
  
  2. **CISTO HIDÁTICO CARDÍACO:**
     - Raro, mas grave
     - Risco de ruptura e anafilaxia
     - Requer tratamento específico (albendazol + cirurgia)
     - **Investigar acometimento hepático concomitante**
  
  3. **TUMORES CARDÍACOS:**
     - Mixoma, fibroelastoma, metástases
     - Avaliar necessidade de ressecção
     - **Estadiamento oncológico se suspeita de malignidade**
  
  ### 📝 ESTRUTURA DA RESPOSTA PARA ECOCARDIOGRAMA:
  
  #### 1. RESUMO DO EXAME
  - Tipo (ETT, ETE, Eco de estresse)
  - Data
  - Qualidade técnica
  
  #### 2. FUNÇÃO SISTÓLICA
  - Fração de ejeção (%)
  - Contratilidade segmentar
  - Débito cardíaco
  
  #### 3. FUNÇÃO DIASTÓLICA
  - Padrão de enchimento
  - Grau de disfunção diastólica
  - Pressões de enchimento
  
  #### 4. VALVAS
  - Mitral, aórtica, tricúspide, pulmonar
  - Estenoses / insuficiências
  
  #### 5. PRESSÕES PULMONARES
  - PSAP estimada
  - Sinais de HAP
  
  #### 6. ⚠️ MASSAS/TROMBOS/CISTOS (se presentes)
  - Localização exata
  - Dimensões
  - Características (móvel, séssil, cístico, sólido)
  - **Risco associado e conduta específica**
  
  #### 7. CLASSIFICAÇÃO DA CARDIOMIOPATIA
  - **OBRIGATÓRIO especificar o tipo:**
    - Cardiomiopatia cirrótica (disfunção diastólica, FE preservada)
    - Cardiomiopatia dilatada (FE reduzida, dilatação, hipocinesia difusa)
    - Cardiomiopatia isquêmica (alterações segmentares)
    - Sem cardiomiopatia
  
  #### 8. ACHADOS CRÍTICOS PARA TRANSPLANTE
  - Elegibilidade cardiológica
  - Fatores de risco
  
  #### 9. RECOMENDAÇÕES
  - Exames adicionais se necessário (cateterismo, RNM cardíaca)
  - Otimização pré-operatória
  - **Se massa/trombo: conduta específica**
  
  #### 10. RESUMO ESTRUTURADO
  
  \`\`\`ACHADOS_EXAME
  TIPO: Ecocardiograma / Cateterismo Cardíaco
  DATA: [data]
  FRACAO_EJECAO: [percentual]%
  FUNCAO_SISTOLICA: [normal / disfunção leve / moderada / grave]
  FUNCAO_DIASTOLICA: [normal / grau I / II / III]
  PSAP: [valor] mmHg
  VALVOPATIAS: [descrição ou AUSENTES]
  DERRAME_PERICARDICO: [SIM - volume / NÃO]
  HAP: [NÃO / LEVE / MODERADA / GRAVE]
  MASSA_INTRACARDICA: [NÃO / SIM - tipo, localização, tamanho]
  TIPO_CARDIOMIOPATIA: [CIRRÓTICA / DILATADA / ISQUÊMICA / AUSENTE]
  ELEGIBILIDADE_TX: [APTO / APTO COM RESSALVAS / CONTRAINDICADO]
  CONCLUSAO: [resumo com impacto para transplante e justificativa]
  \`\`\`
  
  ---
  
  **⚠️ OBRIGATÓRIO — LABEL LITERAL:** A linha de abertura do bloco acima DEVE conter o label literal \`ACHADOS_EXAME\` após as três crases, exatamente como no exemplo. Não use apenas \`\`\` sozinho — o parser depende do label literal para recuperar a estrutura.
  `;
}

/**
 * Template para PROVA DE FUNÇÃO PULMONAR
 */
export function getTemplatePulmonar(): string {
  return `
  ## ⚠️ REGRAS ESPECÍFICAS PARA FUNÇÃO PULMONAR:
  
  ### 🫁 FOCO DA ANÁLISE - PRÉ-TRANSPLANTE HEPÁTICO:
  - ✅ Capacidade vital forçada (CVF)
  - ✅ Volume expiratório forçado (VEF1)
  - ✅ Relação VEF1/CVF
  - ✅ Difusão de CO (DLCO) - importante para síndrome hepatopulmonar
  - ✅ Padrão ventilatório (obstrutivo, restritivo, misto)
  - ✅ Resposta ao broncodilatador
  
  ### 📊 PADRÕES VENTILATÓRIOS:
  | Padrão | VEF1/CVF | CVF | VEF1 |
  |--------|----------|-----|------|
  | Normal | > 70% | > 80% | > 80% |
  | Obstrutivo | < 70% | Normal ou ↓ | ↓ |
  | Restritivo | Normal ou ↑ | < 80% | < 80% |
  | Misto | < 70% | < 80% | ↓↓ |
  
  ### 🔴 SÍNDROME HEPATOPULMONAR:
  - DLCO < 80% do previsto
  - Gradiente alvéolo-arterial de O2 aumentado
  - Shunt intrapulmonar
  
  ### 📝 ESTRUTURA DA RESPOSTA PARA FUNÇÃO PULMONAR:
  
  #### 1. RESUMO DO EXAME
  - Tipo de exame
  - Data
  - Qualidade técnica
  
  #### 2. VOLUMES E CAPACIDADES
  - CVF (valor e % do previsto)
  - VEF1 (valor e % do previsto)
  - VEF1/CVF
  
  #### 3. DIFUSÃO (se realizada)
  - DLCO (valor e % do previsto)
  
  #### 4. PADRÃO VENTILATÓRIO
  - Classificação
  - Gravidade
  
  #### 5. ACHADOS CRÍTICOS PARA TRANSPLANTE
  - Elegibilidade pulmonar
  - Risco cirúrgico
  
  #### 6. RECOMENDAÇÕES
  - Otimização pré-operatória
  - Exames adicionais
  
  #### 7. RESUMO ESTRUTURADO
  
  \`\`\`ACHADOS_EXAME
  TIPO: Prova de Função Pulmonar
  DATA: [data]
  CVF: [valor] L ([percentual]% do previsto)
  VEF1: [valor] L ([percentual]% do previsto)
  VEF1_CVF: [percentual]%
  DLCO: [valor ou NÃO REALIZADA] ([percentual]% do previsto)
  PADRAO_VENTILATORIO: [NORMAL / OBSTRUTIVO / RESTRITIVO / MISTO]
  GRAVIDADE: [NORMAL / LEVE / MODERADO / GRAVE]
  RESPOSTA_BD: [POSITIVA / NEGATIVA / NÃO REALIZADA]
  RISCO_CIRURGICO: [BAIXO / MODERADO / ALTO]
  CONCLUSAO: [resumo com impacto para transplante]
  \`\`\`
  
  ---
  
  **⚠️ OBRIGATÓRIO — LABEL LITERAL:** A linha de abertura do bloco acima DEVE conter o label literal \`ACHADOS_EXAME\` após as três crases, exatamente como no exemplo. Não use apenas \`\`\` sozinho — o parser depende do label literal para recuperar a estrutura.
  `;
}

/**
 * Template para ELASTOGRAFIA
 */
export function getTemplateElastografia(): string {
  return `
  ## ⚠️ REGRAS ESPECÍFICAS PARA ELASTOGRAFIA:
  
  ### 📊 FOCO DA ANÁLISE:
  - ✅ Rigidez hepática (kPa)
  - ✅ IQR/mediana (qualidade da medida)
  - ✅ Correlação com estágio de fibrose (METAVIR)
  - ✅ CAP (se disponível) - esteatose
  
  ### 📈 CLASSIFICAÇÃO DE FIBROSE (FibroScan):
  | Rigidez (kPa) | METAVIR | Interpretação |
  |---------------|---------|---------------|
  | < 7,0 | F0-F1 | Sem fibrose / fibrose mínima |
  | 7,0 - 9,5 | F2 | Fibrose significativa |
  | 9,5 - 12,5 | F3 | Fibrose avançada |
  | > 12,5 | F4 | Cirrose |
  
  ### 📈 CAP (Controlled Attenuation Parameter):
  | CAP (dB/m) | Grau | Interpretação |
  |------------|------|---------------|
  | < 238 | S0 | Sem esteatose |
  | 238-259 | S1 | Esteatose leve |
  | 260-290 | S2 | Esteatose moderada |
  | > 290 | S3 | Esteatose grave |
  
  ### 📝 ESTRUTURA DA RESPOSTA PARA ELASTOGRAFIA:
  
  #### 1. RESUMO DO EXAME
  - Tipo de exame
  - Data
  - Qualidade técnica
  
  #### 2. RIGIDEZ HEPÁTICA
  - Valor em kPa
  - IQR/mediana
  - Qualidade da medida
  
  #### 3. ESTEATOSE (CAP)
  - Valor em dB/m
  - Grau de esteatose
  
  #### 4. INTERPRETAÇÃO
  - Estágio de fibrose METAVIR
  - Correlação clínica
  
  #### 5. ACHADOS CRÍTICOS
  - Presença de cirrose
  - Impacto clínico
  
  #### 6. RECOMENDAÇÕES
  - Seguimento
  - Exames complementares
  
  #### 7. RESUMO ESTRUTURADO
  
  \`\`\`ACHADOS_EXAME
  TIPO: Elastografia Hepática
  DATA: [data]
  RIGIDEZ: [valor] kPa
  IQR_MEDIANA: [percentual]% (qualidade: BOA se <30%)
  FIBROSE_METAVIR: [F0-F4]
  CAP: [valor] dB/m (S0-S3) ou NÃO REALIZADO
  INTERPRETACAO: [descrição]
  CONCLUSAO: [resumo do estágio de fibrose/cirrose]
  \`\`\`
  
  ---
  
  **⚠️ OBRIGATÓRIO — LABEL LITERAL:** A linha de abertura do bloco acima DEVE conter o label literal \`ACHADOS_EXAME\` após as três crases, exatamente como no exemplo. Não use apenas \`\`\` sozinho — o parser depende do label literal para recuperar a estrutura.
  `;
}

/**
 * Template para TC TÓRAX (Estadiamento Oncológico)
 */
export function getTemplateTorax(cenario: CenarioClinico): string {
  // ============================================================
  // BRANCH 1 — ESTADIAMENTO PRÉ-TX (cenário oncológico legítimo)
  // Preserva o template original que funcionava bem neste cenário.
  // ============================================================
  if (cenario === "estadiamento_pre_tx") {
    return `
  ## ⚠️ REGRAS ESPECÍFICAS PARA TC/RM TÓRAX — ESTADIAMENTO ONCOLÓGICO

  ### 🎯 OBJETIVO PRINCIPAL: DESCARTAR METÁSTASES INTRATORÁCICAS
  O exame foi solicitado em contexto de estadiamento oncológico (pré-transplante ou
  vigilância CHC ativa). A análise deve COMEÇAR por esta avaliação e dar a ela o MAIOR DESTAQUE.

  ### 🚫 O QUE NÃO FAZER:
  - ❌ NÃO avaliar fígado detalhadamente (exame torácico não é hepático)
  - ❌ NÃO classificar lesões hepáticas por LI-RADS
  - ❌ NÃO avaliar critérios de Milão
  - ❌ NÃO avaliar veia porta ou trombose portal
  - ❌ NÃO criticar ausência de contraste para avaliação hepática
  - ❌ NÃO sugerir TC/RM abdome (provavelmente já existe no histórico)

  ### ✅ O QUE AVALIAR (por ordem de prioridade):
  1. **Metástases pulmonares** — presença/ausência de nódulos suspeitos
  2. **Linfonodomegalias mediastinais/hilares** — estadiamento N
  3. **Derrame pleural** — pode indicar disseminação
  4. **Nódulos pulmonares** — classificar por Fleischner se indeterminados
  5. **Achados incidentais** — lesões ósseas, mediastinais, etc.
  6. **Achados da transição toracoabdominal** — apenas mencionar brevemente

  ### 📊 CLASSIFICAÇÃO DE NÓDULOS PULMONARES (Fleischner Society):
  | Tamanho | Risco baixo | Risco alto (paciente oncológico) |
  |---------|-------------|----------------------------------|
  | < 6mm | Sem seguimento | TC 12 meses |
  | 6-8mm | TC 6-12 meses | TC 6-12 meses, considerar PET |
  | > 8mm | TC 3 meses ou PET | TC 3 meses, PET, considerar biópsia |

  ⚠️ **Paciente com CHC ativo = SEMPRE alto risco para nódulos pulmonares.**

  ### 📝 ESTRUTURA DA RESPOSTA:

  #### 1. RESUMO DO EXAME
  - Tipo, data, protocolo (com/sem contraste)

  #### 2. 🔴 ESTADIAMENTO ONCOLÓGICO (SEÇÃO PRINCIPAL)
  - **Metástases pulmonares:** PRESENTE / AUSENTE
  - Se ausentes: "Ausência de lesões pulmonares suspeitas de metástases — estadiamento torácico NEGATIVO"
  - Se presentes: localização, número, tamanho, características
  - **Linfonodomegalias:** mediastinais, hilares (>10mm eixo curto = suspeita)
  - **Derrame pleural:** presença, lateralidade, volume estimado
  - **Lesões ósseas:** metástases esqueléticas

  #### 3. NÓDULOS PULMONARES (se presentes)
  - Número, localização, tamanho
  - Características (sólido, vidro fosco, misto, calcificado)
  - Classificação Fleischner adaptada para paciente oncológico

  #### 4. ACHADOS PULMONARES INCIDENTAIS
  - Enfisema, bronquiolite, intersticiopatia
  - Broncopatia inflamatória
  - Relevância para risco cirúrgico

  #### 5. ACHADOS MEDIASTINAIS E OUTROS
  - Coração e grandes vasos
  - Lesões mediastinais (se houver)
  - Coluna/parede torácica

  #### 6. TRANSIÇÃO TORACOABDOMINAL (BREVE)
  - Mencionar apenas achados NOVOS visíveis
  - Referir: "Avaliação hepática completa disponível nas TC/RM de abdome já realizadas"

  #### 7. RECOMENDAÇÕES POR IMAGEM
  - Seguimento de nódulos indeterminados (protocolo Fleischner)
  - Achados que impactam risco cirúrgico
  - NÃO sugerir exames abdominais (já realizados)

  #### 8. RESUMO ESTRUTURADO

  \`\`\`ACHADOS_EXAME
  TIPO: Tomografia/Ressonância de Tórax
  DATA: [data]
  CENARIO: estadiamento_pre_tx
  METASTASES_PULMONARES: [AUSENTES / PRESENTES - número, localização, tamanho]
  NODULOS_PULMONARES: [AUSENTES / número e tamanho - classificar Fleischner]
  LINFONODOMEGALIAS: [AUSENTES / localização e tamanho]
  DERRAME_PLEURAL: [NÃO / SIM - lateralidade e volume]
  LESOES_OSSEAS: [NÃO / SIM - localização]
  ESTADIAMENTO_TORACICO: [NEGATIVO / POSITIVO / INDETERMINADO]
  ACHADOS_INCIDENTAIS: [descrever se relevantes]
  RISCO_CIRURGICO_PULMONAR: [BAIXO / MODERADO / ALTO]
  CONCLUSAO: [estadiamento oncológico torácico em 1-2 frases]
  \`\`\`

  ---

  **⚠️ OBRIGATÓRIO — LABEL LITERAL:** A linha de abertura do bloco acima DEVE conter o label literal \`ACHADOS_EXAME\` após as três crases, exatamente como no exemplo. Não use apenas \`\`\` sozinho — o parser depende do label literal para recuperar a estrutura.
  `;
  }

  // ============================================================
  // BRANCH 2 — PÓS-TX COM SINTOMA (imunossuprimido sintomático)
  // Foco em complicações de imunossupressão crônica.
  // Resolve o caso Francisco (tosse há 1 sem, pós-Tx 14 anos).
  // ============================================================
  if (cenario === "pos_tx_com_sintoma") {
    return `
  ## ⚠️ REGRAS ESPECÍFICAS PARA TC/RM TÓRAX — PÓS-TRANSPLANTE COM SINTOMA AGUDO

  ### 🎯 ENQUADRAMENTO CLÍNICO:
  Paciente TRANSPLANTADO hepático apresentando sintoma agudo (ver bloco "Contexto
  clínico do exame" acima). Este exame **NÃO é de estadiamento oncológico** — foi
  solicitado para investigar a queixa declarada pelo médico solicitante.

  ### 📚 DIFERENCIAIS PRIORITÁRIOS (imunossupressão crônica):

  1. **Infecção oportunista**
     - Bacteriana (incluindo atípicas: Legionella, Mycoplasma, Nocardia)
     - Viral (CMV, EBV, HSV, Influenza, SARS-CoV-2)
     - Fúngica (Aspergillus, Pneumocystis jirovecii — PCP, Cryptococcus, Candida)
     - Tuberculose (reativação ou primo-infecção em imunossuprimido)

  2. **PTLD (Post-Transplant Lymphoproliferative Disorder)**
     - Frequentemente EBV-associado
     - Pode manifestar-se como nódulos pulmonares, massa mediastinal ou linfonodomegalia
     - Incidência aumentada nos primeiros 5 anos pós-Tx, mas possível a qualquer tempo

  3. **Toxicidade medicamentosa pulmonar**
     - **Pneumonite por inibidores de mTOR** (sirolimus, everolimus) — padrão mais comum: vidro fosco bilateral, possível intersticiopatia
     - Toxicidade por outros imunossupressores (raros)

  4. **Doenças crônicas associadas à imunossupressão de longa data**
     - Bronquiectasias por infecções repetidas
     - Broncopatia inflamatória crônica com colonização (Pseudomonas, Aspergillus)
     - Fibrose pulmonar

  5. **Eventos vasculares e cardíacos**
     - Embolismo pulmonar (risco aumentado em pós-Tx com síndrome metabólica)
     - Congestão pulmonar (cardiopatia isquêmica pós-Tx frequente)

  ### 🚫 REGRAS DE ENQUADRAMENTO:

  - ❌ NÃO iniciar a análise com "estadiamento oncológico" — esse não é o propósito do exame
  - ❌ NÃO presumir recidiva de CHC sem gatilho radiológico específico (nódulo hipervascular novo, massa sólida atípica)
  - ❌ NÃO classificar granulomas calcificados residuais, atelectasias ou bronquiectasias como "suspeitos de metástase"
  - ❌ NÃO criticar ausência de contraste se o exame foi pedido para investigar sintoma respiratório (TC sem contraste é apropriado para investigação infecciosa)
  - ✅ RESPONDER à indicação clínica declarada — se foi pedido por tosse, o raciocínio primário é infeccioso/inflamatório
  - ✅ MENCIONAR achados oncológicos APENAS se houver gatilho radiológico claro
  - ✅ RECONHECER limitações da imunossupressão na interpretação (infecções podem ter apresentação atípica)

  ### 📝 ESTRUTURA DA RESPOSTA:

  #### 1. RESUMO DO EXAME
  - Tipo, data, protocolo
  - **Reafirmar a indicação clínica declarada** como âncora da análise

  #### 2. 🩺 RESPOSTA À INDICAÇÃO CLÍNICA (SEÇÃO PRINCIPAL)
  - Correlacionar os achados do laudo com a queixa do paciente
  - Organizar o raciocínio a partir dos diferenciais priorizados acima
  - Se o laudo sugere padrão específico (consolidação, vidro fosco, árvore em brotamento, cavitação), nomear o diferencial radiológico mais provável
  - Indicar se os achados explicam, parcialmente explicam ou não explicam a queixa

  #### 3. ACHADOS PRINCIPAIS (lista descritiva)
  - Vias aéreas (brônquios, bronquíolos)
  - Parênquima pulmonar (infiltrados, nódulos, cavitações, vidro fosco)
  - Mediastino e hilos (linfonodomegalias — avaliar contexto PTLD se presentes)
  - Pleura (derrame, espessamento)
  - Coração e grandes vasos
  - Parede torácica e ossos

  #### 4. DIAGNÓSTICO DIFERENCIAL RADIOLÓGICO
  - Listar 2 a 4 diferenciais mais prováveis com base no padrão identificado
  - Para CADA diferencial: que achado favorece, o que seria necessário para confirmar/excluir
  - Usar o campo "DIFERENCIAIS" do resumo estruturado

  #### 5. ACHADOS INCIDENTAIS
  - Granulomas calcificados (sequelares, sem relevância oncológica default)
  - Alterações degenerativas (espondilose, ateromatose)
  - Outros achados crônicos

  #### 6. RELEVÂNCIA PARA O ENXERTO E IMUNOSSUPRESSÃO
  - Se há sinais sugestivos de toxicidade por mTOR → mencionar
  - Se há sinais de infecção oportunista específica → correlacionar com protocolos de tratamento (sem prescrever doses — apenas indicação)

  #### 7. RECOMENDAÇÕES POR IMAGEM E CORRELAÇÃO CLÍNICA
  - Exames complementares de imagem que ajudariam no diferencial (sem repetir o que já existe)
  - Correlação clínica sugerida (marcadores laboratoriais, culturas, broncoscopia se pertinente)
  - NÃO sugerir conduta terapêutica específica (doses, drogas, intervalos) — isso é escopo da Análise Geral

  #### 8. RESUMO ESTRUTURADO

  \`\`\`ACHADOS_EXAME
  TIPO: Tomografia/Ressonância de Tórax
  DATA: [data]
  CENARIO: pos_tx_com_sintoma
  INDICACAO_DECLARADA: [repetir indicação clínica declarada]
  HIPOTESE_PRIMARIA: [diferencial radiológico mais provável]
  DIFERENCIAIS: [2-4 hipóteses secundárias em ordem de probabilidade]
  ACHADOS_INFECCIOSOS: [consolidação / vidro fosco / cavitação / árvore em brotamento / AUSENTES]
  ACHADOS_VASCULARES: [embolismo / congestão / AUSENTES]
  ACHADOS_INTERSTICIAIS: [intersticiopatia / fibrose / toxicidade mTOR / AUSENTES]
  LINFONODOMEGALIAS: [AUSENTES / localização e tamanho - considerar PTLD se presentes]
  DERRAME_PLEURAL: [NÃO / SIM - lateralidade e volume]
  NODULOS_PULMONARES: [AUSENTES / características - só classificar como suspeitos se gatilho radiológico específico]
  ACHADOS_INCIDENTAIS: [granulomas / bronquiectasias / sequelares / etc]
  CORRELACAO_INDICACAO: [achados explicam / parcialmente explicam / não explicam a queixa declarada]
  CONCLUSAO: [resposta em 1-2 frases à indicação clínica, seguida da hipótese radiológica mais provável]
  \`\`\`

  ---

  **⚠️ OBRIGATÓRIO — LABEL LITERAL:** A linha de abertura do bloco acima DEVE conter o label literal \`ACHADOS_EXAME\` após as três crases, exatamente como no exemplo. Não use apenas \`\`\` sozinho — o parser depende do label literal para recuperar a estrutura.
  `;
  }

  // ============================================================
  // BRANCH 3 — SINTOMA AGUDO (não pós-Tx ou contexto não oncológico)
  // Foco em diferencial infeccioso/inflamatório/vascular padrão.
  // ============================================================
  if (cenario === "sintoma_agudo") {
    return `
  ## ⚠️ REGRAS ESPECÍFICAS PARA TC/RM TÓRAX — INVESTIGAÇÃO DE SINTOMA AGUDO

  ### 🎯 ENQUADRAMENTO CLÍNICO:
  O exame foi solicitado para investigar SINTOMA AGUDO (ver bloco "Contexto clínico
  do exame" acima). Este exame **NÃO é de estadiamento oncológico** — foi solicitado
  para responder à queixa declarada pelo médico solicitante.

  ### 📚 DIFERENCIAIS PRIORITÁRIOS (conforme sintoma):

  **Se tosse/dispneia/febre:**
  - Infecção respiratória (bacteriana, viral, atípica)
  - Bronquite aguda / pneumonia
  - Exacerbação de doença crônica (DPOC, bronquiectasias)

  **Se dor torácica:**
  - Embolismo pulmonar (se dispneia associada)
  - Pneumotórax
  - Pleurite / derrame pleural
  - Dor musculoesquelética (diagnóstico de exclusão)

  **Se hemoptise:**
  - Bronquite crônica / bronquiectasias
  - Infecção (TB, aspergiloma)
  - Neoplasia pulmonar primária (investigar)
  - Embolismo pulmonar

  ### 🚫 REGRAS DE ENQUADRAMENTO:

  - ❌ NÃO iniciar a análise com "estadiamento oncológico"
  - ❌ NÃO presumir cenário oncológico sem gatilho radiológico específico
  - ❌ NÃO classificar achados inflamatórios/sequelares como "suspeitos de metástase" por default
  - ✅ RESPONDER à indicação clínica declarada com raciocínio diagnóstico dirigido
  - ✅ MENCIONAR achados oncológicos APENAS se houver gatilho radiológico claro
  - ✅ ACHADOS SEQUELARES (granulomas calcificados, fibrose residual) devem ser reportados como tal, não como suspeitos

  ### 📝 ESTRUTURA DA RESPOSTA:

  #### 1. RESUMO DO EXAME
  - Tipo, data, protocolo
  - **Reafirmar a indicação clínica declarada** como âncora

  #### 2. 🩺 RESPOSTA À INDICAÇÃO CLÍNICA (SEÇÃO PRINCIPAL)
  - Correlacionar os achados com a queixa
  - Nomear padrão radiológico identificado
  - Indicar se os achados explicam, parcialmente explicam ou não explicam a queixa

  #### 3. ACHADOS PRINCIPAIS
  - Vias aéreas e parênquima pulmonar
  - Mediastino e hilos
  - Pleura e derrame
  - Coração e grandes vasos
  - Parede torácica e ossos

  #### 4. DIAGNÓSTICO DIFERENCIAL RADIOLÓGICO
  - 2-4 diferenciais mais prováveis baseados no padrão
  - Para cada: o que favorece, o que seria necessário para confirmar

  #### 5. ACHADOS INCIDENTAIS
  - Achados crônicos sem relação direta com a queixa

  #### 6. RECOMENDAÇÕES POR IMAGEM E CORRELAÇÃO CLÍNICA
  - Exames complementares se diferencial não estreitado
  - Correlação clínica sugerida
  - NÃO sugerir conduta terapêutica específica

  #### 7. RESUMO ESTRUTURADO

  \`\`\`ACHADOS_EXAME
  TIPO: Tomografia/Ressonância de Tórax
  DATA: [data]
  CENARIO: sintoma_agudo
  INDICACAO_DECLARADA: [repetir indicação clínica declarada]
  HIPOTESE_PRIMARIA: [diferencial radiológico mais provável]
  DIFERENCIAIS: [2-4 hipóteses secundárias]
  ACHADOS_INFECCIOSOS: [consolidação / vidro fosco / cavitação / AUSENTES]
  ACHADOS_VASCULARES: [embolismo / congestão / AUSENTES]
  ACHADOS_INTERSTICIAIS: [intersticiopatia / fibrose / AUSENTES]
  DERRAME_PLEURAL: [NÃO / SIM - lateralidade e volume]
  LINFONODOMEGALIAS: [AUSENTES / localização]
  NODULOS_PULMONARES: [AUSENTES / características]
  ACHADOS_INCIDENTAIS: [sequelares / degenerativos]
  CORRELACAO_INDICACAO: [achados explicam / parcialmente explicam / não explicam a queixa]
  CONCLUSAO: [resposta em 1-2 frases à indicação clínica]
  \`\`\`

  ---

  **⚠️ OBRIGATÓRIO — LABEL LITERAL:** A linha de abertura do bloco acima DEVE conter o label literal \`ACHADOS_EXAME\` após as três crases, exatamente como no exemplo. Não use apenas \`\`\` sozinho — o parser depende do label literal para recuperar a estrutura.
  `;
  }

  // ============================================================
  // BRANCH 4 — PÓS-TX ROTINA + INDETERMINADO (descritivo neutro)
  // Sem hipótese dirigida. Descrição e diferencial abertos.
  // ============================================================
  return `
  ## ⚠️ REGRAS ESPECÍFICAS PARA TC/RM TÓRAX — ANÁLISE DESCRITIVA NEUTRA

  ### 🎯 ENQUADRAMENTO CLÍNICO:
  O contexto clínico deste exame não se enquadra em estadiamento oncológico ativo
  nem em investigação de sintoma agudo (ver bloco "Contexto clínico do exame"
  acima). A análise deve ser **descritiva e neutra**, sem hipótese diagnóstica
  dirigida.

  ${
    cenario === "pos_tx_rotina"
      ? `### 📌 CONTEXTO: paciente PÓS-TRANSPLANTE em seguimento de rotina.
  Diferenciais a ter em mente quando houver achados relevantes:
  - Complicações do enxerto (se exame topograficamente compatível)
  - Complicações da imunossupressão (infecções crônicas, PTLD)
  - Recidiva da doença de base (se o histórico favorecer)
  - Doenças crônicas associadas à imunossupressão de longa data`
      : `### 📌 CONTEXTO: indicação clínica não documentada ou não classificável.
  Sem gatilho específico para enquadramento oncológico, infeccioso ou vascular dirigido.
  Recomenda-se que a indicação clínica seja documentada em exames futuros.`
  }

  ### 🚫 REGRAS DE ENQUADRAMENTO:

  - ❌ NÃO presumir cenário oncológico sem gatilho radiológico específico
  - ❌ NÃO classificar nódulos sequelares, granulomas, atelectasias como "suspeitos" por default
  - ❌ NÃO iniciar a análise com "estadiamento torácico"
  - ✅ REPORTAR achados de forma descritiva e neutra
  - ✅ MENCIONAR diferenciais abertos somente se houver achados que justifiquem
  - ✅ RECOMENDAR correlação clínica quando os achados forem inespecíficos

  ### 📝 ESTRUTURA DA RESPOSTA:

  #### 1. RESUMO DO EXAME
  - Tipo, data, protocolo

  #### 2. ACHADOS PRINCIPAIS (descritivo)
  - Vias aéreas e parênquima pulmonar
  - Mediastino e hilos
  - Pleura
  - Coração e grandes vasos
  - Parede torácica e ossos
  - Transição toracoabdominal (breve)

  #### 3. AVALIAÇÃO DOS ACHADOS
  - Para cada achado relevante: caracterizar radiologicamente sem hipótese dirigida
  - Se há padrão específico: mencionar diferencial aberto (ex: "nódulo subpleural sólido, sem critérios definitivos de benignidade/malignidade — seguimento conforme Fleischner")
  - Granulomas calcificados, bronquiectasias, alterações degenerativas: reportar como achados crônicos/sequelares sem sugerir malignidade

  #### 4. DIFERENCIAIS (se aplicável)
  - Listar diferenciais abertos APENAS quando houver achado que justifique
  - Em pós-Tx, considerar complicações de imunossupressão como possibilidade

  #### 5. ACHADOS INCIDENTAIS
  - Sequelares, degenerativos, sem relevância imediata

  #### 6. RECOMENDAÇÕES POR IMAGEM
  - Seguimento de nódulos indeterminados (Fleischner)
  - Correlação clínica se achados forem inespecíficos
  - NÃO sugerir conduta terapêutica

  #### 7. RESUMO ESTRUTURADO

  \`\`\`ACHADOS_EXAME
  TIPO: Tomografia/Ressonância de Tórax
  DATA: [data]
  CENARIO: ${cenario}
  INDICACAO_DECLARADA: [indicação declarada ou "não documentada"]
  ACHADOS_PULMONARES: [descrição dos achados principais]
  ACHADOS_MEDIASTINAIS: [descrição]
  ACHADOS_PLEURAIS: [descrição]
  NODULOS_PULMONARES: [AUSENTES / características descritivas]
  LINFONODOMEGALIAS: [AUSENTES / localização]
  ACHADOS_INCIDENTAIS: [sequelares, degenerativos]
  DIFERENCIAIS_ABERTOS: [lista se aplicável / NÃO APLICÁVEL]
  CONCLUSAO: [descrição neutra dos achados em 1-2 frases]
  \`\`\`

  ---

  **⚠️ OBRIGATÓRIO — LABEL LITERAL:** A linha de abertura do bloco acima DEVE conter o label literal \`ACHADOS_EXAME\` após as três crases, exatamente como no exemplo. Não use apenas \`\`\` sozinho — o parser depende do label literal para recuperar a estrutura.
  `;
}
/**
 * Template para CPRE
 */
export function getTemplateCPRE(): string {
  return `
  ## ⚠️ REGRAS ESPECÍFICAS PARA CPRE:

  ### 🎯 FOCO DA ANÁLISE:
  - ✅ Anatomia das vias biliares (intra e extra-hepáticas)
  - ✅ Estenoses biliares (localização, extensão, tipo)
  - ✅ Cálculos biliares (coledocolitíase)
  - ✅ Dilatação de vias biliares
  - ✅ Procedimentos realizados (papilotomia, prótese, dilatação)
  - ✅ Ducto pancreático (se avaliado)
  - ✅ Complicações do procedimento

  ### 📊 CLASSIFICAÇÃO DE ESTENOSES BILIARES PÓS-TX:
  | Tipo | Localização | Causa provável | Tratamento |
  |------|-------------|----------------|------------|
  | Anastomótica | Junção biliar (coledoco-coledoco ou hepaticojejuno) | Isquemia, tensão, técnica | CPRE + dilatação + prótese |
  | Não-anastomótica | Intra-hepática difusa | Isquemia (TAH), CAST | CTPH ou retransplante |
  | Não-anastomótica | Hilar focal | Isquemia parcial | CPRE ± CTPH |

  ### ⚠️ LIMITAÇÕES DA CPRE:
  - CPRE acessa apenas vias biliares EXTRA-HEPÁTICAS e porção proximal das intra-hepáticas
  - Em CAST difusa: CPRE tem acesso LIMITADO às estenoses intra-hepáticas
  - Se estenoses intra-hepáticas difusas → considerar CTPH como complemento

  ### 📝 ESTRUTURA DA RESPOSTA:

  #### 1. RESUMO DO EXAME
  - Data, indicação, sedação

  #### 2. VIA BILIAR EXTRA-HEPÁTICA
  - Colédoco (calibre, estenoses, cálculos)
  - Hepático comum
  - Anastomose (se pós-Tx)

  #### 3. VIAS BILIARES INTRA-HEPÁTICAS
  - Dilatação (focal/difusa)
  - Estenoses (padrão, localização)
  - Acesso obtido ou limitado

  #### 4. DUCTO PANCREÁTICO (se avaliado)
  - Calibre, irregularidades

  #### 5. PROCEDIMENTOS REALIZADOS
  - Papilotomia, extração de cálculos, prótese (tipo, tamanho), dilatação

  #### 6. COMPLICAÇÕES
  - Sangramento, perfuração, pancreatite pós-CPRE

  #### 7. RECOMENDAÇÕES
  - Troca de prótese (quando), seguimento, necessidade de CTPH complementar

  #### 8. RESUMO ESTRUTURADO

  \`\`\`ACHADOS_EXAME
  TIPO: CPRE
  DATA: [data]
  COLEDOCO: [calibre, estenoses, cálculos]
  VIAS_BILIARES_INTRAHEPATICAS: [normais / dilatadas / estenoses - padrão]
  ANASTOMOSE_BILIAR: [pérvia / estenose - grau / NÃO APLICÁVEL]
  CALCULOS: [SIM - número e localização / NÃO]
  PROCEDIMENTO_REALIZADO: [papilotomia, prótese, dilatação - detalhes]
  COMPLICACOES: [NÃO / SIM - tipo]
  ACESSO_INTRAHEPATICO: [COMPLETO / LIMITADO / NÃO OBTIDO]
  CONCLUSAO: [resumo em 1-2 frases]
  \`\`\`
  
  ---
  
  **⚠️ OBRIGATÓRIO — LABEL LITERAL:** A linha de abertura do bloco acima DEVE conter o label literal \`ACHADOS_EXAME\` após as três crases, exatamente como no exemplo. Não use apenas \`\`\` sozinho — o parser depende do label literal para recuperar a estrutura.
  `;
}

/**
 * Template para ECOENDOSCOPIA (USE)
 */
export function getTemplateEcoendoscopia(): string {
  return `
  ## ⚠️ REGRAS ESPECÍFICAS PARA ECOENDOSCOPIA:

  ### 🎯 FOCO DA ANÁLISE:
  - ✅ Lesões hepáticas (lobo esquerdo e caudado — melhor visualizados)
  - ✅ Via biliar distal e pâncreas
  - ✅ Linfonodos regionais (estadiamento)
  - ✅ Varizes (esofágicas, gástricas, periesofágicas)
  - ✅ Ascite
  - ✅ Lesões subepiteliais gástricas/esofágicas
  - ✅ Punção aspirativa (PAAF) se realizada

  ### 📊 INDICAÇÕES PRINCIPAIS EM HEPATOLOGIA:
  | Indicação | O que avaliar |
  |-----------|---------------|
  | CHC: dúvida diagnóstica | Lesão, vascularização, PAAF |
  | Colangiocarcinoma | Massa hilar, invasão vascular, linfonodos |
  | Varizes gástricas | Calibre, fluxo, candidato a cianoacrilato |
  | Lesão pancreática | Massas, IPMN, cistos |
  | Linfonodomegalia | Estadiamento, PAAF |

  ### 📝 ESTRUTURA DA RESPOSTA:

  #### 1. RESUMO DO EXAME
  - Data, indicação, sedação

  #### 2. ACHADOS HEPATOBILIARES
  - Lesões focais hepáticas (lobo esquerdo/caudado)
  - Via biliar (calibre, estenoses, cálculos)
  - Vesícula biliar

  #### 3. PÂNCREAS
  - Parênquima, lesões focais, ducto pancreático

  #### 4. VARIZES E HIPERTENSÃO PORTAL
  - Varizes esofágicas/gástricas/periesofágicas
  - Calibre e fluxo

  #### 5. LINFONODOS
  - Localização, tamanho, características (benignos vs suspeitos)

  #### 6. PUNÇÃO (se realizada)
  - Local, número de passes, complicações
  - Resultado preliminar (se disponível)

  #### 7. RECOMENDAÇÕES

  #### 8. RESUMO ESTRUTURADO

  \`\`\`ACHADOS_EXAME
  TIPO: Ecoendoscopia
  DATA: [data]
  LESOES_HEPATICAS: [descrição ou NENHUMA]
  VIA_BILIAR: [normal / dilatada / estenose - localização]
  PANCREAS: [normal / lesão - descrição]
  VARIZES: [tipo, calibre / AUSENTES]
  LINFONODOS: [normais / suspeitos - localização e tamanho]
  PAAF_REALIZADA: [SIM - local / NÃO]
  ASCITE: [SIM / NÃO]
  CONCLUSAO: [resumo em 1-2 frases]
  \`\`\`
  
  ---
  
  **⚠️ OBRIGATÓRIO — LABEL LITERAL:** A linha de abertura do bloco acima DEVE conter o label literal \`ACHADOS_EXAME\` após as três crases, exatamente como no exemplo. Não use apenas \`\`\` sozinho — o parser depende do label literal para recuperar a estrutura.
  `;
}

/**
 * Template para COLANGIOGRAFIA TRANSPARIETO-HEPÁTICA (CTPH)
 */
export function getTemplateCTPH(): string {
  return `
  ## ⚠️ REGRAS ESPECÍFICAS PARA CTPH:

  ### 🎯 FOCO DA ANÁLISE:
  - ✅ Anatomia biliar intra-hepática (acesso SUPERIOR à CPRE)
  - ✅ Estenoses intra-hepáticas (localização, extensão, padrão)
  - ✅ Dilatação segmentar/lobar
  - ✅ Comunicação entre segmentos
  - ✅ Drenagem realizada (cateter, calibre, posição)
  - ✅ Complicações (sangramento, biloma, pneumotórax)

  ### 📊 INDICAÇÕES PRINCIPAIS:
  | Indicação | Contexto |
  |-----------|----------|
  | CAST com estenoses intra-hepáticas | CPRE insuficiente |
  | Estenose biliar alta (Bismuth III-IV) | Acesso endoscópico impossível |
  | Hepaticojejunostomia sem acesso endoscópico | Anatomia alterada |
  | Mapeamento pré-retransplante | Planejamento cirúrgico |
  | Drenagem biliar paliativa | Colestase obstrutiva |

  ### ⚠️ AVALIAÇÃO ESPECÍFICA EM CAST:
  - Padrão de estenoses: difuso ("contas de rosário") vs focal
  - Segmentos drenáveis vs segmentos excluídos
  - Comunicação entre ductos direitos e esquerdos
  - Eficácia da drenagem (redução de bilirrubina esperada)
  - Em CAST difusa: drenagem pode ser PALIATIVA, não curativa — retransplante é o tratamento definitivo

  ### 📝 ESTRUTURA DA RESPOSTA:

  #### 1. RESUMO DO PROCEDIMENTO
  - Data, indicação, acesso (lobo direito/esquerdo), complicações imediatas

  #### 2. VIAS BILIARES INTRA-HEPÁTICAS
  - Dilatação (segmentar, lobar, difusa)
  - Estenoses (localização, padrão, grau)
  - Comunicação intersegmentar

  #### 3. CONFLUÊNCIA BILIAR
  - Perviedade da confluência dos hepáticos
  - Classificação de Bismuth (se estenose hilar)

  #### 4. DRENAGEM REALIZADA
  - Tipo de cateter (externo, interno-externo)
  - Posição final, calibre
  - Dilatação de estenose (sim/não)

  #### 5. COMPLICAÇÕES
  - Sangramento, biloma, pneumotórax, sepse biliar

  #### 6. RECOMENDAÇÕES
  - Troca/reposicionamento de cateter (quando)
  - Avaliação de resposta (bilirrubina em 48-72h)
  - Necessidade de retransplante se CAST difusa

  #### 7. RESUMO ESTRUTURADO

  \`\`\`ACHADOS_EXAME
  TIPO: Colangiografia Transparieto-hepática (CTPH)
  DATA: [data]
  ACESSO: [lobo direito / esquerdo / bilateral]
  ESTENOSES_INTRAHEPATICAS: [focal - localização / difusas - padrão / AUSENTES]
  DILATACAO: [segmentar / lobar / difusa - especificar]
  COMUNICACAO_INTERSEGMENTAR: [PRESENTE / AUSENTE / PARCIAL]
  CONFLUENCIA_BILIAR: [pérvia / obstruída - Bismuth]
  DRENAGEM_REALIZADA: [SIM - tipo, posição / NÃO]
  COMPLICACOES: [NÃO / SIM - tipo]
  EFICACIA_ESTIMADA: [BOA / LIMITADA - justificar]
  CONCLUSAO: [resumo em 1-2 frases incluindo se paliativo ou terapêutico]
  \`\`\`
  
  ---
  
  **⚠️ OBRIGATÓRIO — LABEL LITERAL:** A linha de abertura do bloco acima DEVE conter o label literal \`ACHADOS_EXAME\` após as três crases, exatamente como no exemplo. Não use apenas \`\`\` sozinho — o parser depende do label literal para recuperar a estrutura.
  `;
}

/**
 * Template para COLONOSCOPIA
 */
export function getTemplateColonoscopia(): string {
  return `
  ## ⚠️ REGRAS ESPECÍFICAS PARA COLONOSCOPIA:

  ### 🎯 FOCO DA ANÁLISE EM PACIENTE HEPATOPATA:
  - ✅ Pólipos (número, tamanho, localização, histologia se disponível)
  - ✅ Lesões suspeitas de malignidade
  - ✅ Doença inflamatória intestinal
  - ✅ Colite (infecciosa, isquêmica, medicamentosa)
  - ✅ Divertículos
  - ✅ Varizes retais/colonicas (hipertensão portal)
  - ✅ Colonopatia hipertensiva portal
  - ✅ Qualidade do preparo
  - ✅ Intubação cecal (exame completo?)

  ### 📊 RASTREAMENTO PRÉ-TRANSPLANTE:
  - Colonoscopia é OBRIGATÓRIA no workup pré-Tx para pacientes > 45 anos
  - Rastreamento de neoplasia colorretal (especialmente em CEP/colangite esclerosante)
  - Pacientes com CEP: colonoscopia ANUAL (risco elevado de câncer colorretal)

  ### ⚠️ ACHADOS ESPECÍFICOS EM HIPERTENSÃO PORTAL:
  - **Varizes retais** (diferenciar de hemorroidas internas)
  - **Colonopatia hipertensiva** (edema, eritema, padrão vascular alterado)
  - **Risco hemorrágico** aumentado por coagulopatia e plaquetopenia

  ### 📝 ESTRUTURA DA RESPOSTA:

  #### 1. RESUMO DO EXAME
  - Data, indicação, preparo, sedação, intubação cecal

  #### 2. RETO E SIGMÓIDE
  - Mucosa, pólipos, varizes retais, hemorroidas

  #### 3. CÓLON DESCENDENTE, TRANSVERSO E ASCENDENTE
  - Mucosa, pólipos, divertículos, lesões

  #### 4. CECO E ÍLEO TERMINAL
  - Intubação cecal confirmada, mucosa

  #### 5. ACHADOS RELEVANTES PARA HEPATOPATA
  - Varizes retais/colonicas
  - Colonopatia hipertensiva portal
  - Lesões que impactam elegibilidade para Tx

  #### 6. BIÓPSIAS (se realizadas)
  - Local, número, indicação

  #### 7. RECOMENDAÇÕES
  - Seguimento conforme achados e risco
  - Se CEP: lembrar colonoscopia anual

  #### 8. RESUMO ESTRUTURADO

  \`\`\`ACHADOS_EXAME
  TIPO: Colonoscopia
  DATA: [data]
  PREPARO: [BOM / REGULAR / RUIM]
  INTUBACAO_CECAL: [SIM / NÃO]
  POLIPOS: [NÃO / SIM - número, localização, tamanho, ressecados?]
  DIVERTÍCULOS: [NÃO / SIM - localização]
  VARIZES_RETAIS: [NÃO / SIM - calibre]
  COLONOPATIA_HIPERTENSIVA: [NÃO / SIM - grau]
  LESOES_SUSPEITAS: [NÃO / SIM - descrição]
  BIOPSIAS: [NÃO / SIM - local]
  NEOPLASIA_COLORRETAL: [AUSENTE / SUSPEITA / CONFIRMADA]
  CONCLUSAO: [resumo em 1-2 frases]
  \`\`\`
  
  ---
  
  **⚠️ OBRIGATÓRIO — LABEL LITERAL:** A linha de abertura do bloco acima DEVE conter o label literal \`ACHADOS_EXAME\` após as três crases, exatamente como no exemplo. Não use apenas \`\`\` sozinho — o parser depende do label literal para recuperar a estrutura.
  `;
}

/**
 * Template para MEDICINA NUCLEAR (PET-CT, Cintilografia, Mapeamento Ósseo).
 * AI_138 (B301 cont.) — antes caía no template de imagem hepática ("isto não é
 * uma tomografia"). Análise METABÓLICA/FUNCIONAL, não morfológica.
 */
export function getTemplateNuclear(): string {
  return `
  ## ⚠️ REGRAS ESPECÍFICAS PARA MEDICINA NUCLEAR (PET-CT / CINTILOGRAFIA):

  ### 🎯 ADAPTE-SE AO EXAME APRESENTADO:
  Este é um exame de MEDICINA NUCLEAR — a análise é METABÓLICA/FUNCIONAL, não morfológica.
  - ❌ NÃO aplique critérios de LI-RADS nem de Milão (são de imagem seccional; não se aplicam aqui).
  - ❌ NÃO trate o exame como uma tomografia/ressonância de fígado.
  - ✅ FOQUE no que o exame realmente avalia: atividade metabólica, captação do traçador e doença à distância.

  ### 🔬 FOCO DA ANÁLISE:
  - ✅ Traçador utilizado (¹⁸F-FDG, ⁶⁸Ga-PSMA/DOTATATE, ⁹⁹ᵐTc-MDP para osso, etc.)
  - ✅ Lesões HIPERMETABÓLICAS: localização e SUVmax
  - ✅ DOENÇA EXTRA-HEPÁTICA / metástases (linfonodos, ossos, pulmão, peritônio)
  - ✅ Resposta metabólica quando houver exame prévio (comparar SUVmax)

  ### ⚠️ INTERPRETAÇÃO NO CONTEXTO HEPATOLÓGICO/TRANSPLANTE:
  - O ¹⁸F-FDG-PET tem SENSIBILIDADE LIMITADA para CHC bem diferenciado (baixa avidez glicolítica) — um PET negativo NÃO exclui CHC. É mais útil para CHC pouco diferenciado, colangiocarcinoma, tumores neuroendócrinos e, sobretudo, para RASTREIO DE DOENÇA EXTRA-HEPÁTICA (estadiamento pré-transplante/pré-cirurgia).
  - Cintilografia / mapeamento ósseo: rastreio de metástases ósseas.
  - NÃO substitui a imagem seccional na avaliação intra-hepática nem os critérios de fila (Milão são por TC/RM).

  ### 📝 ESTRUTURA DA RESPOSTA:

  #### 1. RESUMO DO EXAME
  - Tipo, traçador, data e qualidade técnica

  #### 2. ACHADOS METABÓLICOS
  - Lesões hipermetabólicas (localização, SUVmax)
  - Captação fisiológica vs patológica

  #### 3. DOENÇA À DISTÂNCIA
  - Metástases / linfonodos / disseminação extra-hepática

  #### 4. COMPARAÇÃO (se houver exame prévio)
  - Resposta metabólica (SUVmax atual vs prévio)

  #### 5. INTERPRETAÇÃO E IMPACTO CLÍNICO
  - Significado para estadiamento / candidatura a transplante / conduta

  #### 6. RECOMENDAÇÕES
  - Seguimento e correlação com imagem seccional

  #### 7. RESUMO ESTRUTURADO

  \`\`\`ACHADOS_EXAME
  TIPO: [PET-CT / Cintilografia / Mapeamento Ósseo]
  DATA: [data]
  TRACADOR: [FDG / PSMA / DOTATATE / MDP-99mTc / outro]
  LESOES_HIPERMETABOLICAS: [localização(ões) ou AUSENTES]
  SUV_MAX: [valor] ou NÃO APLICÁVEL
  DOENCA_EXTRA_HEPATICA: [SIM - descrição / NÃO]
  RESPOSTA_METABOLICA: [comparação com prévio ou NÃO APLICÁVEL]
  CONCLUSAO: [síntese em 1-2 frases com impacto clínico]
  \`\`\`

  ---

  **⚠️ OBRIGATÓRIO — LABEL LITERAL:** A linha de abertura do bloco acima DEVE conter o label literal \`ACHADOS_EXAME\` após as três crases, exatamente como no exemplo. Não use apenas \`\`\` sozinho — o parser depende do label literal para recuperar a estrutura.
  `;
}

/**
 * Template para INTERVENÇÃO LOCORREGIONAL (TACE/quimioembolização, ablação
 * RFA/MWA, radioembolização/TARE-Y90). AI_138 (B301 cont.) — antes caía no
 * template de imagem hepática. Avalia técnica e RESPOSTA por mRECIST.
 */
export function getTemplateIntervencao(): string {
  return `
  ## ⚠️ REGRAS ESPECÍFICAS PARA INTERVENÇÃO LOCORREGIONAL (TACE / ABLAÇÃO / RADIOEMBOLIZAÇÃO):

  ### 🎯 IDENTIFIQUE O TIPO DE DOCUMENTO:
  Pode ser (a) o RELATÓRIO DO PROCEDIMENTO (o que foi feito) ou (b) uma IMAGEM DE CONTROLE pós-procedimento (avaliação de resposta). Adapte a análise ao que foi apresentado.
  - Se for RELATÓRIO: descreva técnica, lesão(ões)-alvo tratada(s) e resultado imediato/intercorrências.
  - Se for CONTROLE por imagem: avalie a RESPOSTA por **mRECIST** (realce arterial VIÁVEL residual), NÃO por RECIST 1.1 — em CHC tratado, o que importa é tecido com realce arterial, não o tamanho total do nódulo (que pode conter necrose/lipiodol).

  ### 🔬 FOCO DA ANÁLISE:
  - ✅ Técnica: cTACE (lipiodol) / DEB-TACE / TARE-Y90 / RFA / MWA
  - ✅ Lesão(ões)-alvo: localização, número, tamanho
  - ✅ Resposta locorregional (mRECIST): Completa / Parcial / Estável / Progressão
  - ✅ Realce arterial VIÁVEL residual ou recidiva local
  - ✅ Complicações: síndrome pós-embolização, abscesso, lesão biliar, refluxo não-alvo, insuficiência hepática

  ### 📊 mRECIST (referência):
  | Resposta | Definição |
  |----------|-----------|
  | RC | Desaparecimento de todo REALCE ARTERIAL nas lesões-alvo |
  | RP | ↓ ≥30% na soma dos diâmetros da porção VIÁVEL (com realce) |
  | DE | Nem RP nem DP |
  | DP | ↑ ≥20% na soma dos diâmetros da porção viável, ou lesão nova |

  ### 📝 ESTRUTURA DA RESPOSTA:

  #### 1. RESUMO DO PROCEDIMENTO/EXAME
  - Tipo, data, técnica

  #### 2. LESÃO-ALVO E TÉCNICA
  - Alvo(s), abordagem, material

  #### 3. RESPOSTA / RESULTADO
  - mRECIST (se controle) ou resultado imediato (se relatório)
  - Realce viável residual / recidiva

  #### 4. COMPLICAÇÕES
  - Presentes ou ausentes

  #### 5. INTERPRETAÇÃO E IMPACTO CLÍNICO
  - Downstaging/bridge para transplante, necessidade de re-tratamento

  #### 6. RECOMENDAÇÕES
  - Seguimento (imagem de controle em intervalo concreto) e próximos passos

  #### 7. RESUMO ESTRUTURADO

  \`\`\`ACHADOS_EXAME
  TIPO: [TACE / Ablação / Radioembolização]
  DATA: [data]
  TECNICA: [cTACE / DEB-TACE / TARE-Y90 / RFA / MWA]
  LESAO_ALVO: [localização, número, tamanho]
  RESPOSTA_MRECIST: [RC / RP / DE / DP / NÃO APLICÁVEL]
  REALCE_VIAVEL_RESIDUAL: [SIM - localização / NÃO / NÃO AVALIÁVEL]
  COMPLICACOES: [descrição ou AUSENTES]
  CONCLUSAO: [síntese em 1-2 frases com impacto para o transplante/conduta]
  \`\`\`

  ---

  **⚠️ OBRIGATÓRIO — LABEL LITERAL:** A linha de abertura do bloco acima DEVE conter o label literal \`ACHADOS_EXAME\` após as três crases, exatamente como no exemplo. Não use apenas \`\`\` sozinho — o parser depende do label literal para recuperar a estrutura.
  `;
}

/**
 * Template GENÉRICO ADAPTATIVO (Fase D, AI_138) para documentos que NÃO são
 * imagem hepático-abdominal: crânio, raio-X e o "Outros" do dropdown — inclui
 * ficha/oferta de DOADOR, resposta da CENTRAL DE TRANSPLANTES e relatórios
 * administrativos/clínicos. Objetivo (pedido de Leonardo): análise LIVRE e
 * FOCADA no conteúdo real, sem forçar a estrutura de imagem de fígado.
 */
export function getTemplateGenerico(): string {
  return `
  ## 📄 ANÁLISE LIVRE E ADAPTATIVA DO DOCUMENTO APRESENTADO:

  Você recebeu um DOCUMENTO que **não é** um exame de imagem hepática padrão. Pode ser:
  - Um exame de OUTRO órgão/sistema (crânio, raio-X de tórax, etc.);
  - Uma FICHA/RELATÓRIO administrativo ou do fluxo de transplante (ex.: resposta da Central de Transplantes, ficha/oferta de ÓRGÃO DE DOADOR, relatório de junta/comitê);
  - Outro documento clínico.

  ### 🧭 COMO ANALISAR:
  1. **PRIMEIRO identifique O QUE É o documento** — NÃO presuma que é uma tomografia/imagem de fígado.
  2. **Analise de forma LIVRE e FOCADA no conteúdo REAL** — descreva e interprete o que o documento efetivamente traz.
  3. ❌ **NÃO** force critérios de LI-RADS, Milão, lesões hepáticas, trombose portal ou estadiamento de CHC se não forem pertinentes ao documento.
  4. ✅ Se for pertinente ao acompanhamento hepatológico/transplante, traga o SIGNIFICADO CLÍNICO e os próximos passos.

  ### 🩺 SE FOR FICHA DE DOADOR / OFERTA DE ÓRGÃO / CENTRAL DE TRANSPLANTES:
  Extraia e organize os dados relevantes para a decisão: dados do doador (idade, causa do óbito, tempo de UTI/aminas, sorologias, função hepática/enzimas, esteatose se referida), compatibilidade (ABO, antropometria) e a decisão/orientação da central. Sinalize contraindicações ou pontos de atenção — SEM inventar dados ausentes.

  ### 📝 ESTRUTURA DA RESPOSTA (adapte ao conteúdo):

  #### 1. IDENTIFICAÇÃO DO DOCUMENTO
  - O que é, origem/emissor e data
  - ⚠️ B382-FU (AI_156): identifique o DOCUMENTO, não o paciente. Inclua apenas:
    tipo de documento, data, laboratório/serviço, profissional responsável com
    registro, e o número do bloco/exame quando houver. NÃO escreva nome,
    prontuário, CPF nem DATA DE NASCIMENTO — nem entre parênteses. Idade e sexo
    podem aparecer. (Caso real de 01/11: a data de nascimento foi copiada do
    cabeçalho do PDF para esta seção.)

  #### 2. CONTEÚDO / ACHADOS RELEVANTES
  - Os pontos objetivos do documento

  #### 3. INTERPRETAÇÃO / SIGNIFICADO CLÍNICO
  - O que significa no contexto do paciente/transplante

  #### 4. PONTOS DE ATENÇÃO E PRÓXIMOS PASSOS
  - Alertas, pendências e conduta sugerida (quando pertinente)

  #### 5. RESUMO ESTRUTURADO

  \`\`\`ACHADOS_EXAME
  TIPO: [o que o documento é — ex.: Ficha de Doador, Resposta da Central de Transplantes, Raio-X de Tórax, TC de Crânio]
  DATA: [data do documento, se houver]
  CONCLUSAO: [síntese em 1-2 frases do que o documento traz e sua relevância]
  \`\`\`

  ---

  **⚠️ OBRIGATÓRIO — LABEL LITERAL:** A linha de abertura do bloco acima DEVE conter o label literal \`ACHADOS_EXAME\` após as três crases, exatamente como no exemplo. Não use apenas \`\`\` sozinho — o parser depende do label literal para recuperar a estrutura.
  `;
}

/**
 * Retorna o template apropriado baseado na categoria
 */
/**
 * DOADOR VIVO DE FÍGADO — B351 (AI_152).
 *
 * ⚠️ Este template NÃO é escolhido pelo tipo do exame, e sim pela MODALIDADE do
 * paciente (ver gerarPromptExameImagem). O motivo é decisivo: a tomografia do
 * doador se chama igual à do receptor ("TC de abdome"), então o tipo não os
 * distingue — e o roteamento por substring deste arquivo já tornou um template
 * inalcançável antes (a ecoendoscopia caindo no de EDA, B305/AI_139). Um exame de
 * imagem num prontuário de DOADOR é, por definição, um exame de doador.
 *
 * A pergunta clínica é outra: não é "que doença este fígado tem?", é "este fígado
 * pode ser dividido com segurança, e como?".
 */
export function getTemplateDoadorFigado(): string {
  return `
## EXAME DE IMAGEM DE DOADOR VIVO DE FÍGADO — ENQUADRAMENTO OBRIGATÓRIO

Este exame pertence a um **DOADOR** em avaliação para transplante intervivos, não
a um paciente com doença hepática. **NÃO** conduza a leitura como rastreio de
doença: um achado incidental deve ser relatado, mas o eixo da análise é
**ADEQUABILIDADE PARA A DOAÇÃO** e **PLANEJAMENTO CIRÚRGICO**.

MELD, Child-Pugh, situação especial e fila de transplante **não devem aparecer no
texto — nem para dizer que não se aplicam**. Ao doador saudável isso é óbvio, e
anunciar a ausência só ocupa espaço. (B351-FU3: a redação anterior proibia
mencionar e o modelo passou a narrar que estava obedecendo.)

### 1. VOLUMETRIA
- Volume hepático total, volume do enxerto previsto e volume do remanescente.
- Se os números permitirem, confira a coerência entre eles (total = enxerto +
  remanescente, dentro do arredondamento). Divergência é erro de medida ou de
  transcrição — **aponte, não corrija em silêncio**.
- **Remanescente do doador**: a segurança de quem doa vem primeiro. Abaixo de
  ~30% do volume total é o limiar clássico de risco de falência pós-hepatectomia.
- 🔎 **Confira a PLAUSIBILIDADE das proporções antes de usá-las.** No fígado
  habitual o lobo direito é ~60-65% do volume total e o esquerdo ~35-40%. Um "lobo
  direito" que apareça com 35-40% do total provavelmente **não é um lobo direito
  inteiro** (pode ser um setor, ou uma medida que exclui o território da veia
  hepática média). Diga isso — proporção implausível é sinal de que o rótulo não
  corresponde ao território.
- **GRWR** (enxerto ÷ peso do receptor): abaixo de ~0,8% associa-se a
  small-for-size. Só comente se o peso do receptor estiver disponível.
- ⚠️ **Compare territórios EQUIVALENTES antes de concluir qualquer coisa.** Laudos
  costumam medir "lobo direito" e "lobo esquerdo **com a veia hepática média**" —
  são definições diferentes, e o território da média pesa muito. Confrontar as duas
  e inferir "dominância" de um lado é comparar coisas distintas. Quando os rótulos
  não descreverem o mesmo plano, a hipótese PRINCIPAL para a assimetria (ou para a
  soma que não fecha) é a diferença de plano — não uma variante anatômica.

### 2. ESTEATOSE
- ⛔ **"Atenuação normal" num estudo CONTRASTADO não é evidência de ausência de
  esteatose.** A avaliação por TC depende da fase SEM contraste (fígado ≤ ~40 UH,
  ou o índice fígado-baço); o realce invalida a medida. Numa angio-TC, portanto, a
  esteatose está **NÃO AVALIADA** — não "favorável", não "sem esteatose
  significativa". Registrar ausência de informação como achado tranquilizador é o
  erro mais perigoso desta seção, porque a esteatose é pilar da aceitabilidade do
  doador. (B351-FU6: a IA cometeu exatamente isso numa validação real.)
- Percentual estimado e método (a estimativa por imagem não substitui biópsia
  quando o resultado for limítrofe).
- Balizas usuais: <10% preferido pela maioria dos centros; até ~30% descrita como
  sem prejuízo consistente de desfecho; graus altos desaconselham a doação.
- Esteatose importa DUAS vezes: reduz a função do enxerto E a do remanescente.

### 3. ANATOMIA — descreva os quatro eixos, mesmo quando habituais
- **Arterial**: descreva o **eixo celíaco** E os ramos hepáticos. O padrão de
  Michels classifica apenas os ramos hepáticos, então "Michels I" **não autoriza**
  chamar o eixo arterial de favorável: a configuração do tronco celíaco importa
  para a dissecção e para o coto arterial. Registre explicitamente tronco
  hepatoesplênico, gástrica esquerda de origem aórtica, tronco celíaco ausente ou
  qualquer origem atípica descrita no laudo — mesmo quando os ramos hepáticos forem
  convencionais. Depois: origem e número de artérias para o enxerto; substituída ×
  acessória; calibre.
- **Portal**: bifurcação habitual, trifurcação ou ramo posterior direito precoce.
  Variação portal frequentemente significa **dois orifícios portais** no enxerto,
  o que muda a reconstrução.
- **Biliar**: número de ductos que ficarão no enxerto. É o eixo que mais pesa na
  morbidade — quanto mais ductos, mais anastomoses e maior risco de fístula.
- **Venoso de drenagem**: veia hepática direita, média e esquerda; presença de
  **veia hepática inferior direita (VHID)** e seu calibre; tributárias **V5 e V8**
  significativas drenando na média.

### 4. IMPLICAÇÕES TÉCNICAS — o que a imagem sugere para a cirurgia
Quando os dados permitirem, aponte explicitamente:
- enxerto mais adequado (lobo direito, lobo esquerdo ou segmento lateral esquerdo);
- inclusão ou não da **veia hepática média** com o enxerto;
- necessidade de **reimplante de VHID** ou de veia acessória;
- necessidade de **reconstrução de V5/V8** com enxerto de interposição;
- número previsto de anastomoses arteriais, portais e biliares.

### ⚖️ REGRA — NÚMERO DERIVADO HERDA A CONFIANÇA DA ORIGEM
(regra de conduta, NÃO uma seção do texto de saída — não numere isto na resposta)
Se você apontou que a volumetria é inconsistente, **não pode em seguida apresentar
GRWR ou remanescente calculados a partir dela como conclusão validada**. Isso é
contradição interna, e ela engana justamente na direção mais perigosa: tranquilizar
sobre a segurança do doador com um número que você mesmo disse não ser confiável.

Quando a origem estiver sob suspeita:
- marque o derivado como **PROVISÓRIO**, não com selo de adequado/favorável;
- diga **para onde o número se move** se a inconsistência se resolver de outro jeito
  (ex.: "se o lobo direito real for ~900 cm³ em vez de 542, o remanescente cai de
  ~63% para ~38% — ainda acima de 30%, mas perto do limiar");
- e deixe explícito o que precisa ser resolvido para que o número valha.

### ⚖️ REGRAS DE HONESTIDADE (valem sobre tudo acima)
(também de conduta — a resposta tem as seções 1 a 4, e só elas são numeradas)
- Se o laudo não traz volumetria, **diga que não traz** — não estime volume a
  partir de descrição qualitativa.
- Não recomende conduta sobre dado ausente: sinalize a lacuna e o que pediria.
- Distinga o que está **escrito no laudo** do que é **inferência sua**.
- **Não declare um eixo "favorável" deixando de fora achado que o laudo descreve
  nele.** Antes de concluir por eixo, releia o laudo inteiro procurando o que
  pertence àquele eixo — inclusive o que aparece longe, em "demais vasos" ou
  "achados adicionais". Omitir um achado descrito é pior que não concluir.
- Nada aqui decide a doação: o texto apoia a decisão da equipe cirúrgica.
`;
}

export function getTemplateEspecifico(
  categoria: string,
  cenario: CenarioClinico = "indeterminado"
): string {
  switch (categoria) {
    case "biopsia":
      return getTemplateBiopsia();
    case "endoscopia":
      return getTemplateEndoscopia();
    case "cardiologico":
      return getTemplateCardiologico();
    case "pulmonar":
      return getTemplatePulmonar();
    case "elastografia":
      return getTemplateElastografia();
    case "torax":
      return getTemplateTorax(cenario);
    case "cpre":
      return getTemplateCPRE();
    case "ecoendoscopia":
      return getTemplateEcoendoscopia();
    case "ctph":
      return getTemplateCTPH();
    case "colonoscopia":
      return getTemplateColonoscopia();
    case "nuclear":
      return getTemplateNuclear();
    case "intervencao":
      return getTemplateIntervencao();
    case "generico":
      return getTemplateGenerico();
    default:
      return ""; // Usa template padrão de imagem hepática
  }
}
